import { useEffect, useState, useRef } from "react";
import { NavLink, Routes, Route, Link, useNavigate, Navigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  House, Link as LinkIcon, Storefront, ChartLineUp, Users, Megaphone, Robot, Wallet,
  ShareNetwork, Gear, SignOut, Lightning, Plus, Trash, Copy, ArrowRight, ArrowUpRight,
  CurrencyDollar, PaperPlaneTilt, InstagramLogo, TiktokLogo, YoutubeLogo, LinkedinLogo,
  FacebookLogo, Sparkle, Gift, CaretDoubleLeft, CaretDoubleRight, Coins, List, X, EnvelopeSimple,
  ShieldCheck, Palette, Check, Funnel as FunnelIcon, UploadSimple, CaretUp, CaretDown, Eye,
} from "@phosphor-icons/react";
import { QRCodeCanvas } from "qrcode.react";
import client, { getUser, saveAuth, clearAuth } from "@/lib/api";
import { isPushSupported, getPushPermission, subscribeToPush, unsubscribeFromPush, currentSubscription, testPush } from "@/lib/push";
import BrandLogo from "@/components/BrandLogo";
import TwoFactorNudge from "@/components/TwoFactorNudge";
import { FunnelStatsModal } from "@/components/FunnelStatsModal";
import { PromoRedeem, AdminPromoCodes } from "@/components/Promo";
import { MUSIC_PLATFORMS, MUSIC_BY_ID } from "@/components/MusicPlatforms";

// -------- Admin: Integrations Status --------
const AdminIntegrations = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    client.get("/admin/integrations").then(r => setData(r.data)).catch(err => toast.error(err?.response?.data?.detail || "Failed"));
  }, []);
  if (!data) return <div className="text-[#A1A1AA] font-mono-k">Loading…</div>;
  const s = data.summary || {};
  return (
    <div>
      <PageHead eyebrow="ADMIN · INTEGRATIONS" title="Payment & email keys — masked status." />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-t border-l border-white/10 mb-6">
        <div className="border-r border-b border-white/10 p-5 bg-[#111318]">
          <div className="text-[10px] font-mono-k tracking-widest-k text-[#A1A1AA] mb-2">PAYPAL LIVE</div>
          <div className={`font-display text-2xl ${s.paypal_live_ready ? "text-[#FACC15]" : "text-red-400"}`} data-testid="int-paypal">{s.paypal_live_ready ? "READY" : "NOT READY"}</div>
        </div>
        <div className="border-r border-b border-white/10 p-5 bg-[#111318]">
          <div className="text-[10px] font-mono-k tracking-widest-k text-[#A1A1AA] mb-2">CRYPTO (USDT)</div>
          <div className={`font-display text-2xl ${s.crypto_ready ? "text-[#FACC15]" : "text-red-400"}`} data-testid="int-crypto">{s.crypto_ready ? "READY" : "NOT READY"}</div>
        </div>
        <div className="border-r border-b border-white/10 p-5 bg-[#111318]">
          <div className="text-[10px] font-mono-k tracking-widest-k text-[#A1A1AA] mb-2">ALL GREEN</div>
          <div className={`font-display text-2xl ${s.all_green ? "text-[#FACC15]" : "text-red-400"}`} data-testid="int-all">{s.all_green ? "YES" : "NO"}</div>
        </div>
      </div>

      <div className="mb-4 p-4 border border-[#FACC15]/40 bg-[#FACC15]/5 text-xs font-mono-k text-[#DDDDDD]">
        <div className="text-[#FACC15] tracking-widest-k mb-2">— WHERE THIS RUNS</div>
        <div>Frontend URL: <span className="text-white">{data.environment.frontend_url || "(not set)"}</span></div>
        <div>Email sender: <span className="text-white">{data.environment.email_from || "(default)"}</span></div>
        <div className="mt-2 text-[#A1A1AA]">If any red boxes below appear on korporatelinks.com (production) but green on preview, the env vars didn't carry over on last deploy — open your Emergent deployment settings and paste them there, then click Redeploy.</div>
      </div>

      <div className="space-y-3">
        {data.checks.map(c => (
          <div key={c.id} className={`border p-4 ${c.ok ? "border-white/10 bg-[#111318]" : "border-red-500/40 bg-red-500/5"}`} data-testid={`int-${c.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`w-2 h-2 ${c.ok ? "bg-[#FACC15]" : "bg-red-500"}`} />
                  <div className="font-mono-k text-xs tracking-widest-k text-white">{c.label.toUpperCase()}</div>
                </div>
                <div className="font-mono-k text-lg text-[#FACC15] break-all">{c.value || "(missing)"}</div>
                <div className="text-xs text-[#A1A1AA] mt-2">{c.required_for}</div>
                <div className="text-[10px] font-mono-k text-[#A1A1AA]/70 mt-1"><span className="text-[#FACC15]">Get it:</span> {c.where_to_get}</div>
              </div>
              <div className={`text-[10px] font-mono-k tracking-widest-k px-2 py-1 border ${c.ok ? "border-[#FACC15] text-[#FACC15]" : "border-red-500 text-red-400"}`}>
                {c.ok ? "OK" : "MISSING"}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 p-5 border border-white/10 bg-[#111318] text-xs font-mono-k text-[#DDDDDD]">
        <div className="text-[#FACC15] tracking-widest-k mb-3">— HOW TO ADD KEYS MANUALLY ON PRODUCTION</div>
        <ol className="space-y-2 list-decimal list-inside">
          <li>Open your Emergent chat → click your <span className="text-white">deployed app</span> in the sidebar → <span className="text-white">Environment Variables</span></li>
          <li>Paste each key from the "MISSING" boxes above — one env var per row</li>
          <li>Save → click <span className="text-[#FACC15]">Redeploy</span> so the new env vars take effect</li>
          <li>Come back to this page and confirm every box is green</li>
        </ol>
      </div>
    </div>
  );
};

// -------- Admin Payouts --------
const AdminPayouts = () => {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [note, setNote] = useState({});
  const [txHash, setTxHash] = useState({});
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    Promise.all([
      client.get(`/admin/payouts?status=${statusFilter}`),
      client.get("/admin/stats"),
    ]).then(([r, s]) => { setRows(r.data); setStats(s.data); }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [statusFilter]);

  const approve = async (id) => {
    try {
      await client.post(`/admin/payouts/${id}/approve`, { note: note[id] || "", tx_hash: txHash[id] || "" });
      toast.success("Payout approved & buyer notified");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const reject = async (id) => {
    if (!confirm("Reject this payout? Funds will be refunded to the user's wallet.")) return;
    try {
      await client.post(`/admin/payouts/${id}/reject`, { note: note[id] || "Insufficient KYC / verification" });
      toast.success("Payout rejected & funds refunded");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <PageHead eyebrow="ADMIN · PAYOUTS" title="Approve or reject withdrawal requests." />

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-t border-l border-white/10 mb-6">
          <div className="border-r border-b border-white/10 p-5 bg-[#111318]">
            <div className="text-[10px] font-mono-k tracking-widest-k text-[#A1A1AA] mb-2">PENDING</div>
            <div className="font-display text-3xl text-[#FACC15]" data-testid="admin-pending-count">{stats.pending_payouts}</div>
          </div>
          <div className="border-r border-b border-white/10 p-5 bg-[#111318]">
            <div className="text-[10px] font-mono-k tracking-widest-k text-[#A1A1AA] mb-2">TO PAY</div>
            <div className="font-display text-3xl text-[#FACC15]" data-testid="admin-pending-total">${stats.pending_total.toFixed(2)}</div>
          </div>
          <div className="border-r border-b border-white/10 p-5 bg-[#111318]">
            <div className="text-[10px] font-mono-k tracking-widest-k text-[#A1A1AA] mb-2">USERS</div>
            <div className="font-display text-3xl">{stats.users}</div>
          </div>
          <div className="border-r border-b border-white/10 p-5 bg-[#111318]">
            <div className="text-[10px] font-mono-k tracking-widest-k text-[#A1A1AA] mb-2">PRO USERS</div>
            <div className="font-display text-3xl">{stats.pro_users}</div>
          </div>
        </div>
      )}

      <div className="flex gap-2 mb-4">
        {["pending", "paid", "rejected", "all"].map(s => (
          <button key={s} onClick={() => setStatusFilter(s)} className={`k-btn text-xs ${statusFilter === s ? "primary" : ""}`} data-testid={`admin-filter-${s}`}>{s.toUpperCase()}</button>
        ))}
      </div>

      {loading ? (
        <div className="p-8 text-[#A1A1AA] font-mono-k">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="border border-white/10 p-8 text-center text-[#A1A1AA] font-mono-k">No {statusFilter} payouts.</div>
      ) : (
        <div className="space-y-3">
          {rows.map(r => (
            <div key={r.id} className="border border-white/10 bg-[#111318] p-5" data-testid={`payout-${r.id}`}>
              <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
                <div>
                  <div className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA]">
                    REF · {r.id.slice(-8)} · {(r.created_at || "").slice(0, 16).replace("T", " ")}
                  </div>
                  <div className="font-display text-2xl text-[#FACC15]">${Math.abs(r.amount).toFixed(2)}</div>
                  <div className="text-sm mt-1">
                    <span className="text-[#A1A1AA]">To</span> <span className="font-mono-k text-white">{r.destination}</span>
                  </div>
                  <div className="text-xs font-mono-k text-[#A1A1AA] mt-1">
                    <span className="text-white">{r.user_name}</span> · {r.user_email} · @{r.user_slug}
                  </div>
                </div>
                <div className={`text-xs font-mono-k tracking-widest-k px-2 py-1 border ${r.status === "paid" ? "border-[#FACC15] text-[#FACC15]" : r.status === "rejected" ? "border-red-500 text-red-400" : "border-white/30 text-[#A1A1AA]"}`}>
                  {(r.status || "pending").toUpperCase()}
                </div>
              </div>
              {r.status === "pending" && (
                <div className="grid md:grid-cols-[1fr_1fr_auto_auto] gap-2 mt-3 items-center">
                  <input placeholder="Tx hash (optional)" value={txHash[r.id] || ""} onChange={e => setTxHash({ ...txHash, [r.id]: e.target.value })} className="k-input text-xs" data-testid={`admin-txhash-${r.id}`} />
                  <input placeholder="Note (optional)" value={note[r.id] || ""} onChange={e => setNote({ ...note, [r.id]: e.target.value })} className="k-input text-xs" data-testid={`admin-note-${r.id}`} />
                  <button onClick={() => approve(r.id)} className="k-btn primary text-xs whitespace-nowrap" data-testid={`admin-approve-${r.id}`}><Check size={14} /> APPROVE</button>
                  <button onClick={() => reject(r.id)} className="k-btn text-xs whitespace-nowrap" data-testid={`admin-reject-${r.id}`}><X size={14} /> REJECT</button>
                </div>
              )}
              {r.status === "paid" && r.tx_hash && (
                <div className="text-xs font-mono-k text-[#A1A1AA] mt-2 break-all">Tx: <span className="text-[#FACC15]">{r.tx_hash}</span></div>
              )}
              {r.admin_note && (
                <div className="text-xs font-mono-k text-[#A1A1AA] mt-2">Note: {r.admin_note}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// -------- Admin Referrals (claims + backfill) --------
const AdminReferrals = () => {
  const [tab, setTab] = useState("pending"); // pending | all | orphans
  const [claims, setClaims] = useState([]);
  const [orphans, setOrphans] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState({});
  const loadClaims = () => client.get(`/admin/referral-claims?status=${tab === "orphans" ? "all" : tab}`).then(r => setClaims(r.data)).catch(err => toast.error(err?.response?.data?.detail || "Failed"));
  const loadOrphans = () => client.get(`/admin/users/unattributed?q=${encodeURIComponent(q)}&limit=100`).then(r => setOrphans(r.data)).catch(err => toast.error(err?.response?.data?.detail || "Failed"));
  useEffect(() => {
    if (tab === "orphans") loadOrphans();
    else loadClaims();
  }, [tab]);
  useEffect(() => {
    if (tab !== "orphans") return;
    const t = setTimeout(loadOrphans, 300);
    return () => clearTimeout(t);
  }, [q]);

  const decide = async (cid, verb) => {
    const note = window.prompt(verb === "approve" ? "Note (optional):" : "Reason for rejection (optional):", "") || "";
    setBusy(b => ({ ...b, [cid]: true }));
    try {
      await client.post(`/admin/referral-claims/${cid}/${verb}`, { note });
      toast.success(verb === "approve" ? "Attributed" : "Rejected");
      loadClaims();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    setBusy(b => ({ ...b, [cid]: false }));
  };

  const backfill = async (uid) => {
    const code = window.prompt("Enter the referrer's code (from their /dashboard/referrals):", "");
    if (code === null) return;
    try {
      const { data } = await client.put(`/admin/users/${uid}/referrer`, { referrer_code: code.trim().toLowerCase() });
      toast.success(code ? `Attributed to ${data.referrer_email || data.referred_by}` : "Cleared");
      loadOrphans();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const statusColor = (s) => s === "approved" ? "#22C55E" : s === "rejected" ? "#EF4444" : "#FACC15";

  return (
    <div>
      <PageHead eyebrow="ADMIN · REFERRALS" title="Attribute missing referrals. Approve claims. Clean the ledger." />
      <div className="flex gap-2 mb-4 flex-wrap">
        <button onClick={() => setTab("pending")} className={`k-btn text-xs ${tab === "pending" ? "primary" : ""}`} data-testid="admin-ref-tab-pending">PENDING CLAIMS · {tab === "pending" ? claims.length : ""}</button>
        <button onClick={() => setTab("all")} className={`k-btn text-xs ${tab === "all" ? "primary" : ""}`} data-testid="admin-ref-tab-all">ALL CLAIMS</button>
        <button onClick={() => setTab("orphans")} className={`k-btn text-xs ${tab === "orphans" ? "primary" : ""}`} data-testid="admin-ref-tab-orphans">UNATTRIBUTED USERS</button>
      </div>

      {tab !== "orphans" ? (
        <div className="border border-white/10">
          {claims.length === 0 && <div className="p-6 text-[#A1A1AA] font-mono-k text-sm">No {tab} claims.</div>}
          {claims.map(c => (
            <div key={c.id} className="border-b border-white/10 p-4 flex items-start gap-3 flex-wrap" data-testid={`admin-claim-${c.id}`}>
              <div className="flex-1 min-w-[240px]">
                <div className="text-xs font-mono-k text-[#A1A1AA]">{(c.created_at || "").slice(0, 16).replace("T", " ")}</div>
                <div className="text-sm font-mono-k">
                  <span className="text-[#FACC15]">{c.claimant_email}</span> claims <span className="text-white">{c.referred_email}</span>
                </div>
                {c.reason && <div className="text-xs font-mono-k text-[#DDDDDD] mt-1 whitespace-pre-wrap">{c.reason}</div>}
                {c.referred_current_referrer && <div className="text-[10px] font-mono-k text-red-400 mt-1">⚠ Already attributed to another user — approving will overwrite</div>}
              </div>
              <div className="text-[10px] font-mono-k tracking-widest-k px-2 py-1 border" style={{ color: statusColor(c.status), borderColor: statusColor(c.status) }}>{(c.status || "pending").toUpperCase()}</div>
              {c.status === "pending" && (
                <div className="flex gap-2">
                  <button onClick={() => decide(c.id, "approve")} disabled={busy[c.id]} className="k-btn primary text-xs" data-testid={`admin-claim-approve-${c.id}`}>APPROVE</button>
                  <button onClick={() => decide(c.id, "reject")} disabled={busy[c.id]} className="k-btn text-xs" data-testid={`admin-claim-reject-${c.id}`}>REJECT</button>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div>
          <input placeholder="Search by email, name, or slug…" value={q} onChange={e => setQ(e.target.value)} className="k-input w-full mb-3" data-testid="admin-orphans-search" />
          <div className="border border-white/10">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10 text-xs font-mono-k tracking-widest-k text-[#A1A1AA]">
                  <th className="text-left p-3">EMAIL</th><th className="text-left p-3">NAME</th><th className="text-left p-3">PLAN</th><th className="text-left p-3">JOINED</th><th className="text-right p-3">ACTION</th>
                </tr>
              </thead>
              <tbody className="font-mono-k text-sm">
                {orphans.length === 0 && <tr><td colSpan="5" className="p-6 text-[#A1A1AA]">No unattributed users {q ? "match this search" : "in the system"}.</td></tr>}
                {orphans.map(u => (
                  <tr key={u.id} className="border-b border-white/10" data-testid={`orphan-${u.id}`}>
                    <td className="p-3">{u.email}</td>
                    <td className="p-3 text-[#A1A1AA]">{u.name}</td>
                    <td className="p-3">{(u.plan || "free").toUpperCase()}</td>
                    <td className="p-3 text-[#A1A1AA]">{(u.created_at || "").slice(0, 10)}</td>
                    <td className="p-3 text-right"><button onClick={() => backfill(u.id)} className="k-btn text-xs" data-testid={`orphan-backfill-${u.id}`}>ATTRIBUTE</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// -------- Admin Fees Ledger --------
const AdminFees = () => {
  const [sum, setSum] = useState(null);
  const [txs, setTxs] = useState([]);
  const [busy, setBusy] = useState({});
  const load = () => Promise.all([
    client.get("/admin/fees/summary").then(r => setSum(r.data)),
    client.get("/admin/fees/transactions?limit=100").then(r => setTxs(r.data)),
  ]).catch(err => toast.error(err?.response?.data?.detail || "Failed"));
  useEffect(() => { load(); }, []);

  const claimPool = async (poolId) => {
    setBusy(b => ({ ...b, [poolId]: true }));
    try {
      const { data } = await client.post("/admin/fees/pool/claim", { pool_id: poolId });
      toast.success(`Reclaimed $${data.amount.toFixed(2)}`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    setBusy(b => ({ ...b, [poolId]: false }));
  };

  if (!sum) return <div className="font-mono-k text-[#A1A1AA]">Loading…</div>;

  return (
    <div>
      <PageHead eyebrow="ADMIN · FEES" title="Platform revenue ledger." />
      <div className="grid md:grid-cols-2 gap-0 border-t border-l border-white/10 mb-8">
        <div className="border-r border-b border-white/10 p-6 bg-[#111318]"><div className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA] mb-2">TOTAL ASSIGNED</div><div className="font-display text-4xl text-[#22C55E]" data-testid="fees-assigned">${sum.total_assigned.toFixed(2)}</div><div className="text-[10px] font-mono-k text-[#A1A1AA] mt-1">Credited to platform admin wallet</div></div>
        <div className="border-r border-b border-white/10 p-6 bg-[#111318]"><div className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA] mb-2">UNASSIGNED POOL</div><div className="font-display text-4xl text-[#FACC15]" data-testid="fees-pool">${sum.unassigned_pool_total.toFixed(2)}</div><div className="text-[10px] font-mono-k text-[#A1A1AA] mt-1">{sum.unassigned_pool_items.length} item(s) — collected when ADMIN_EMAILS was empty</div></div>
      </div>

      <div className="mb-8">
        <div className="text-xs font-mono-k tracking-widest-k text-[#FACC15] mb-2">— BY PROVIDER × KIND</div>
        <div className="border border-white/10">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10 text-xs font-mono-k tracking-widest-k text-[#A1A1AA]">
                <th className="text-left p-3">PROVIDER</th><th className="text-left p-3">KIND</th><th className="text-right p-3">COUNT</th><th className="text-right p-3">TOTAL</th>
              </tr>
            </thead>
            <tbody className="font-mono-k text-sm">
              {sum.breakdown.length === 0 && <tr><td colSpan="4" className="p-6 text-[#A1A1AA]">No platform fees collected yet.</td></tr>}
              {sum.breakdown.map((b, i) => (
                <tr key={i} className="border-b border-white/10" data-testid={`fee-row-${i}`}>
                  <td className="p-3">{b.provider.toUpperCase()}</td>
                  <td className="p-3 text-[#A1A1AA]">{b.subtype.replace(/_/g, " ").toUpperCase()}</td>
                  <td className="p-3 text-right">{b.count}</td>
                  <td className="p-3 text-right text-[#22C55E]">${b.total.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {sum.unassigned_pool_items.length > 0 && (
        <div className="mb-8">
          <div className="text-xs font-mono-k tracking-widest-k text-[#FACC15] mb-2">— UNASSIGNED POOL · RECONCILE</div>
          <div className="border border-white/10">
            {sum.unassigned_pool_items.map(p => (
              <div key={p.id} className="border-b border-white/10 p-3 flex items-center justify-between gap-3" data-testid={`pool-${p.id}`}>
                <div className="text-sm font-mono-k min-w-0 flex-1">
                  <div>${p.amount?.toFixed?.(2) || p.amount} · {p.kind || "sale_fee"} · {p.provider || "?"}</div>
                  <div className="text-[10px] text-[#A1A1AA] mt-1">seller {p.seller_id?.slice(-8) || "?"} · {(p.created_at || "").slice(0, 16).replace("T", " ")}</div>
                </div>
                {p.claimed_at ? (
                  <span className="text-[10px] font-mono-k tracking-widest-k text-[#22C55E]">CLAIMED</span>
                ) : (
                  <button onClick={() => claimPool(p.id)} disabled={busy[p.id]} className="k-btn text-xs" data-testid={`pool-claim-${p.id}`}>CLAIM</button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="text-xs font-mono-k tracking-widest-k text-[#FACC15] mb-2">— RECENT FEE TRANSACTIONS · {txs.length}</div>
      <div className="border border-white/10 mb-8">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/10 text-xs font-mono-k tracking-widest-k text-[#A1A1AA]">
              <th className="text-left p-3">DATE</th><th className="text-left p-3">LABEL</th><th className="text-right p-3">AMOUNT</th>
            </tr>
          </thead>
          <tbody className="font-mono-k text-sm">
            {txs.length === 0 && <tr><td colSpan="3" className="p-6 text-[#A1A1AA]">No fee transactions yet.</td></tr>}
            {txs.map(t => (
              <tr key={t.id} className="border-b border-white/10" data-testid={`fee-tx-${t.id}`}>
                <td className="p-3 text-[#A1A1AA]">{(t.created_at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="p-3">{t.label}</td>
                <td className="p-3 text-right text-[#22C55E]">${t.amount?.toFixed?.(2) || t.amount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// -------- Sidebar --------
const nav = [
  { to: "/dashboard", icon: House, label: "OVERVIEW", end: true },
  { to: "/dashboard/links", icon: LinkIcon, label: "BIO LINKS" },
  { to: "/dashboard/shop", icon: Storefront, label: "SHOP" },
  { to: "/dashboard/leads", icon: Users, label: "LEADS" },
  { to: "/dashboard/funnels", icon: FunnelIcon, label: "FUNNELS" },
  { to: "/dashboard/campaigns", icon: Megaphone, label: "CAMPAIGNS" },
  { to: "/dashboard/ai", icon: Robot, label: "AI COPILOT" },
  { to: "/dashboard/social", icon: ShareNetwork, label: "SOCIAL" },
  { to: "/dashboard/appearance", icon: Palette, label: "APPEARANCE" },
  { to: "/dashboard/wallet", icon: Wallet, label: "WALLET" },
  { to: "/dashboard/referrals", icon: Gift, label: "REFERRALS" },
  { to: "/dashboard/analytics", icon: ChartLineUp, label: "ANALYTICS" },
  { to: "/dashboard/pricing", icon: Sparkle, label: "PLAN" },
  { to: "/dashboard/settings", icon: Gear, label: "SETTINGS" },
];

const Sidebar = ({ user, onLogout, collapsed, setCollapsed, mobileOpen, setMobileOpen }) => {
  const closeMobile = () => setMobileOpen(false);
  return (
    <>
      {mobileOpen && <div className="fixed inset-0 bg-black/60 z-30 lg:hidden" onClick={closeMobile} data-testid="sidebar-backdrop" />}
      <aside
        className={`
          fixed lg:sticky top-0 left-0 h-screen z-40 border-r border-white/10 bg-[#0A0D14] flex flex-col
          transition-transform duration-200 lg:transition-[width]
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0
          ${collapsed ? "lg:w-16 w-64" : "w-64"}
        `}
      >
        <div className={`p-4 border-b border-white/10 flex items-center ${collapsed && !mobileOpen ? "lg:justify-center" : "justify-between"}`}>
          <Link to="/" className="flex items-center gap-2" onClick={closeMobile} data-testid="dash-logo">
            <BrandLogo height={(!collapsed || mobileOpen) ? 56 : 40} />
          </Link>
          <button onClick={closeMobile} className="lg:hidden text-[#A1A1AA]" data-testid="sidebar-close"><X size={20} /></button>
        </div>
        <nav className="flex-1 overflow-y-auto py-4">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              title={n.label}
              onClick={closeMobile}
              data-testid={`nav-${n.label.toLowerCase().replace(/ /g,"-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 ${collapsed && !mobileOpen ? "lg:px-0 lg:justify-center px-6" : "px-6"} py-3 text-xs font-mono-k tracking-widest-k border-l-2 transition-colors ${
                  isActive
                    ? "border-[#FACC15] bg-[#111318] text-white"
                    : "border-transparent text-[#A1A1AA] hover:text-white hover:bg-[#111318]/60"
                }`
              }
            >
              <n.icon size={18} weight="duotone" />
              <span className={collapsed && !mobileOpen ? "lg:hidden" : ""}>{n.label}</span>
            </NavLink>
          ))}
          {user?.is_admin && (
            <NavLink
              to="/dashboard/admin/payouts"
              title="Admin"
              onClick={closeMobile}
              data-testid="nav-admin"
              className={({ isActive }) =>
                `flex items-center gap-3 ${collapsed && !mobileOpen ? "lg:px-0 lg:justify-center px-6" : "px-6"} py-3 text-xs font-mono-k tracking-widest-k border-l-2 transition-colors mt-2 border-t border-t-white/10 pt-4 ${
                  isActive
                    ? "border-[#FACC15] bg-[#FACC15]/10 text-[#FACC15]"
                    : "border-transparent text-[#FACC15]/70 hover:text-[#FACC15] hover:bg-[#FACC15]/5"
                }`
              }
            >
              <ShieldCheck size={18} weight="duotone" />
              <span className={collapsed && !mobileOpen ? "lg:hidden" : ""}>ADMIN · PAYOUTS</span>
            </NavLink>
          )}
          {user?.is_admin && (
            <NavLink
              to="/dashboard/admin/fees"
              title="Fees"
              onClick={closeMobile}
              data-testid="nav-admin-fees"
              className={({ isActive }) =>
                `flex items-center gap-3 ${collapsed && !mobileOpen ? "lg:px-0 lg:justify-center px-6" : "px-6"} py-3 text-xs font-mono-k tracking-widest-k border-l-2 transition-colors ${
                  isActive
                    ? "border-[#FACC15] bg-[#FACC15]/10 text-[#FACC15]"
                    : "border-transparent text-[#FACC15]/70 hover:text-[#FACC15] hover:bg-[#FACC15]/5"
                }`
              }
            >
              <CurrencyDollar size={18} weight="duotone" />
              <span className={collapsed && !mobileOpen ? "lg:hidden" : ""}>ADMIN · FEES</span>
            </NavLink>
          )}
          {user?.is_admin && (
            <NavLink
              to="/dashboard/admin/referrals"
              title="Referrals"
              onClick={closeMobile}
              data-testid="nav-admin-referrals"
              className={({ isActive }) =>
                `flex items-center gap-3 ${collapsed && !mobileOpen ? "lg:px-0 lg:justify-center px-6" : "px-6"} py-3 text-xs font-mono-k tracking-widest-k border-l-2 transition-colors ${
                  isActive
                    ? "border-[#FACC15] bg-[#FACC15]/10 text-[#FACC15]"
                    : "border-transparent text-[#FACC15]/70 hover:text-[#FACC15] hover:bg-[#FACC15]/5"
                }`
              }
            >
              <Gift size={18} weight="duotone" />
              <span className={collapsed && !mobileOpen ? "lg:hidden" : ""}>ADMIN · REFERRALS</span>
            </NavLink>
          )}
          {user?.is_admin && (
            <NavLink
              to="/dashboard/admin/integrations"
              title="Integrations"
              onClick={closeMobile}
              data-testid="nav-admin-integrations"
              className={({ isActive }) =>
                `flex items-center gap-3 ${collapsed && !mobileOpen ? "lg:px-0 lg:justify-center px-6" : "px-6"} py-3 text-xs font-mono-k tracking-widest-k border-l-2 transition-colors ${
                  isActive
                    ? "border-[#FACC15] bg-[#FACC15]/10 text-[#FACC15]"
                    : "border-transparent text-[#FACC15]/70 hover:text-[#FACC15] hover:bg-[#FACC15]/5"
                }`
              }
            >
              <Gear size={18} weight="duotone" />
              <span className={collapsed && !mobileOpen ? "lg:hidden" : ""}>ADMIN · KEYS</span>
            </NavLink>
          )}
          {user?.is_admin && (
            <NavLink
              to="/dashboard/admin/promo-codes"
              title="Promo codes"
              onClick={closeMobile}
              data-testid="nav-admin-promo"
              className={({ isActive }) =>
                `flex items-center gap-3 ${collapsed && !mobileOpen ? "lg:px-0 lg:justify-center px-6" : "px-6"} py-3 text-xs font-mono-k tracking-widest-k border-l-2 transition-colors ${
                  isActive
                    ? "border-[#FACC15] bg-[#FACC15]/10 text-[#FACC15]"
                    : "border-transparent text-[#FACC15]/70 hover:text-[#FACC15] hover:bg-[#FACC15]/5"
                }`
              }
            >
              <Sparkle size={18} weight="duotone" />
              <span className={collapsed && !mobileOpen ? "lg:hidden" : ""}>ADMIN · PROMO CODES</span>
            </NavLink>
          )}
        </nav>
        <div className="border-t border-white/10 p-3">
          <button onClick={() => setCollapsed(!collapsed)} className={`hidden lg:flex w-full items-center ${collapsed ? "justify-center" : "justify-between"} text-[#A1A1AA] hover:text-white py-2 text-xs font-mono-k tracking-widest-k`} data-testid="btn-toggle-sidebar">
            {!collapsed && <span>COLLAPSE</span>}
            {collapsed ? <CaretDoubleRight size={16} /> : <CaretDoubleLeft size={16} />}
          </button>
          {(!collapsed || mobileOpen) ? (
            <>
              <div className="mt-2 mb-3 min-w-0 flex items-center gap-3">
                {user?.avatar ? (
                  <img src={user.avatar} alt="" className="w-10 h-10 object-cover border border-[#FACC15] flex-shrink-0" data-testid="sidebar-avatar" />
                ) : (
                  <div className="w-10 h-10 border border-white/20 bg-[#0A0D14] flex items-center justify-center text-sm font-display text-[#FACC15] flex-shrink-0" data-testid="sidebar-avatar-fallback">
                    {(user?.name || user?.email || "K")[0]?.toUpperCase()}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-sm truncate">{user?.name || user?.email}</div>
                  <div className="text-[10px] font-mono-k tracking-widest-k text-[#FACC15]">{(user?.plan || "free").toUpperCase()} PLAN</div>
                </div>
              </div>
              <button onClick={onLogout} className="k-btn w-full justify-center text-xs" data-testid="btn-logout">
                <SignOut size={14} /> LOG OUT
              </button>
            </>
          ) : (
            <>
              {user?.avatar ? (
                <img src={user.avatar} alt="" className="w-8 h-8 object-cover border border-[#FACC15] mx-auto mt-2 mb-2" data-testid="sidebar-avatar-collapsed" />
              ) : (
                <div className="w-8 h-8 border border-white/20 bg-[#0A0D14] flex items-center justify-center text-xs font-display text-[#FACC15] mx-auto mt-2 mb-2">
                  {(user?.name || user?.email || "K")[0]?.toUpperCase()}
                </div>
              )}
              <button onClick={onLogout} className="k-btn w-full justify-center text-xs px-0 py-2" data-testid="btn-logout" title="Log out">
                <SignOut size={16} />
              </button>
            </>
          )}
        </div>
      </aside>
    </>
  );
};

// Mobile top bar with hamburger
const MobileBar = ({ onOpen, user }) => (
  <div className="lg:hidden sticky top-0 z-20 bg-[#0A0D14]/95 backdrop-blur-md border-b border-white/10 flex items-center justify-between px-4 py-3">
    <button onClick={onOpen} className="text-white flex items-center gap-2" data-testid="btn-open-sidebar">
      <List size={22} />
      <BrandLogo height={36} />
    </button>
    <div className="text-xs font-mono-k tracking-widest-k text-[#FACC15]">{(user?.plan || "free").toUpperCase()}</div>
  </div>
);

// -------- Reusable UI --------
const PageHead = ({ eyebrow, title, action }) => (
  <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
    <div>
      <div className="text-xs font-mono-k tracking-widest-k text-[#FACC15] mb-2">— {eyebrow}</div>
      <h1 className="font-display text-4xl tracking-tighter">{title}</h1>
    </div>
    {action}
  </div>
);

const Stat = ({ label, value, sub }) => (
  <div className="border border-white/10 p-6 bg-[#111318]">
    <div className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA] mb-3">{label}</div>
    <div className="font-display text-4xl tracking-tighter">{value}</div>
    {sub && <div className="text-xs font-mono-k text-[#A1A1AA] mt-2">{sub}</div>}
  </div>
);

// -------- Overview --------
const Overview = ({ user }) => {
  const [data, setData] = useState(null);
  const [ref, setRef] = useState(null);
  useEffect(() => {
    client.get("/analytics").then(r => setData(r.data));
    client.get("/referral").then(r => setRef(r.data)).catch(() => {});
  }, []);
  const shareUrl = `${window.location.origin}/u/${user?.slug}`;
  const copy = () => { navigator.clipboard.writeText(shareUrl); toast.success("Link copied"); };
  const copyRef = () => { if (ref?.share_url) { navigator.clipboard.writeText(ref.share_url); toast.success("Referral link copied"); } };
  return (
    <div>
      <PageHead eyebrow="COMMAND CENTER" title={`Welcome, ${user?.name || "operator"}.`}
        action={<button className="k-btn primary" onClick={copy} data-testid="btn-copy-bio"><Copy size={16} /> COPY BIO LINK</button>} />

      <div className="grid md:grid-cols-2 gap-4 mb-8">
        <div className="border border-white/10 bg-[#111318] p-5">
          <div className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA] mb-2">YOUR PUBLIC PAGE</div>
          <div className="font-mono-k text-sm md:text-base text-[#FACC15] break-all mb-3" data-testid="public-url">{shareUrl}</div>
          <div className="flex gap-2">
            <button onClick={copy} className="k-btn text-xs flex-1 justify-center"><Copy size={14} /> COPY</button>
            <a href={`/u/${user?.slug}`} target="_blank" rel="noreferrer" className="k-btn text-xs flex-1 justify-center" data-testid="btn-open-public">OPEN <ArrowUpRight size={14} /></a>
          </div>
        </div>
        {ref?.share_url && (
          <div className="border border-[#FACC15]/40 bg-[#FACC15]/5 p-5" data-testid="overview-ref-card">
            <div className="text-xs font-mono-k tracking-widest-k text-[#FACC15] mb-2 flex items-center gap-2"><Gift size={14} /> SHARE & EARN {ref.commission_pct}%</div>
            <div className="font-mono-k text-sm md:text-base text-white break-all mb-3">{ref.share_url}</div>
            <div className="flex gap-2">
              <button onClick={copyRef} className="k-btn primary text-xs flex-1 justify-center" data-testid="btn-copy-ref-overview"><Copy size={14} /> COPY REFERRAL</button>
              <Link to="/dashboard/referrals" className="k-btn text-xs flex-1 justify-center">DETAILS <ArrowRight size={14} /></Link>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 border-t border-l border-white/10">
        <div className="border-r border-b border-white/10"><Stat label="TOTAL CLICKS" value={data?.total_clicks ?? "—"} /></div>
        <div className="border-r border-b border-white/10"><Stat label="LEADS CAPTURED" value={data?.total_leads ?? "—"} /></div>
        <div className="border-r border-b border-white/10"><Stat label="REVENUE" value={`$${data?.revenue ?? 0}`} /></div>
        <div className="border-r border-b border-white/10"><Stat label="AD SPEND" value={`$${data?.ad_spend ?? 0}`} /></div>
      </div>

      <div className="grid lg:grid-cols-3 gap-0 border-l border-r border-b border-white/10 mt-0">
        <div className="lg:col-span-2 border-r border-white/10 p-6">
          <div className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA] mb-4">CLICKS · LAST 7 DAYS</div>
          <div className="flex items-end gap-2 h-40">
            {(data?.series || []).map((d, i) => {
              const max = Math.max(1, ...(data?.series || []).map(x => x.clicks));
              const h = Math.max(4, (d.clicks / max) * 140);
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-2">
                  <div className="w-full bg-[#FACC15]" style={{ height: h }} />
                  <div className="text-[10px] font-mono-k text-[#A1A1AA]">{d.date.slice(5)}</div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="p-6">
          <div className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA] mb-4">TOP LINKS</div>
          <div className="space-y-3">
            {(data?.top_links || []).length === 0 && <div className="text-sm font-mono-k text-[#A1A1AA]">No links yet.</div>}
            {(data?.top_links || []).map((l) => (
              <div key={l.id} className="flex items-center justify-between border-b border-white/10 pb-2">
                <div className="text-sm truncate">{l.title}</div>
                <div className="text-xs font-mono-k text-[#FACC15]">{l.clicks}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// -------- Links --------
const Links = () => {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ title: "", url: "", icon: "Link", image: "", description: "", active: true, order: 0 });
  const load = () => client.get("/links").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);
  const add = async (e) => {
    e.preventDefault();
    try { await client.post("/links", f); setF({ title: "", url: "", icon: "Link", image: "", description: "", active: true, order: items.length }); load(); toast.success("Link added"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { await client.delete(`/links/${id}`); load(); };
  return (
    <div>
      <PageHead eyebrow="BIO LINKS" title="Curate your one-page empire." />
      <form onSubmit={add} className="border border-white/10 bg-[#111318] p-6 mb-8 grid md:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA]">TITLE</label>
          <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="k-input mt-1" required data-testid="link-title" />
        </div>
        <div>
          <label className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA]">URL</label>
          <input value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} className="k-input mt-1" placeholder="https://…" required data-testid="link-url" />
        </div>
        <div className="md:col-span-2">
          <label className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA]">THUMBNAIL IMAGE URL (optional — Beacons style)</label>
          <input value={f.image} onChange={(e) => setF({ ...f, image: e.target.value })} className="k-input mt-1" placeholder="https://…/image.jpg" data-testid="link-image" />
        </div>
        <div className="md:col-span-2">
          <label className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA]">DESCRIPTION (optional)</label>
          <input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="k-input mt-1" placeholder="Short 1-line subtitle" data-testid="link-desc" />
        </div>
        <button className="k-btn primary md:col-span-2 justify-center" data-testid="link-add"><Plus size={16} /> ADD LINK</button>
      </form>
      <div className="border-t border-l border-white/10">
        {items.length === 0 && <div className="border-r border-b border-white/10 p-6 text-[#A1A1AA] font-mono-k text-sm">No links yet. Add your first above.</div>}
        {items.map((l) => (
          <div key={l.id} className="border-r border-b border-white/10 p-4 flex items-center gap-4 bg-[#0A0D14]" data-testid={`link-row-${l.id}`}>
            {l.image ? (
              <img src={l.image} alt="" className="w-12 h-12 object-cover border border-white/20" />
            ) : (
              <div className="w-12 h-12 border border-white/20 flex items-center justify-center"><LinkIcon size={18} /></div>
            )}
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{l.title}</div>
              {l.description && <div className="text-xs text-[#DDDDDD] truncate">{l.description}</div>}
              <div className="text-xs font-mono-k text-[#A1A1AA] truncate">{l.url}</div>
            </div>
            <div className="text-xs font-mono-k text-[#FACC15]">{l.clicks} clicks</div>
            <button onClick={() => del(l.id)} className="k-btn text-xs" data-testid={`link-del-${l.id}`}><Trash size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

// -------- Shop --------
const Shop = () => {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ name: "", description: "", price: 0, image: "", active: true, stock: 999, delivery_url: "", delivery_note: "", license_keys: [], requires_shipping: false });
  const [keysRaw, setKeysRaw] = useState("");
  const [uploading, setUploading] = useState(false);
  const [me, setMe] = useState(getUser() || {});
  const load = () => client.get("/products").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);
  const toggleBundle = async () => {
    const next = !me.bundle_discount_enabled;
    try {
      const { data } = await client.put("/profile", { bundle_discount_enabled: next });
      setMe(data);
      saveAuth(localStorage.getItem("k_token"), data);
      toast.success(next ? "Bulk discounts ON (5/10/15% at 2/3/4+ items)" : "Bulk discounts OFF");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const add = async (e) => {
    e.preventDefault();
    try {
      const license_keys = keysRaw.split("\n").map(s => s.trim()).filter(Boolean);
      await client.post("/products", { ...f, price: parseFloat(f.price), license_keys });
      setF({ name: "", description: "", price: 0, image: "", active: true, stock: 999, delivery_url: "", delivery_note: "", license_keys: [], requires_shipping: false });
      setKeysRaw("");
      load(); toast.success("Product added");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) return toast.error("Max 3MB");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await client.post("/upload/product-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF(v => ({ ...v, image: data.url }));
      toast.success("Image uploaded");
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    setUploading(false);
    e.target.value = "";
  };
  const del = async (id) => { await client.delete(`/products/${id}`); load(); };
  return (
    <div>
      <PageHead eyebrow="SHOP" title="Sell products, straight from your bio." />
      <div className="border border-white/10 bg-[#111318] p-4 mb-4 flex items-center justify-between flex-wrap gap-3" data-testid="bundle-toggle">
        <div className="flex-1 min-w-[240px]">
          <div className="text-[10px] font-mono-k tracking-widest-k text-[#FACC15]">— BULK DISCOUNTS</div>
          <div className="font-mono-k text-sm">Auto-apply 5% / 10% / 15% off when a buyer adds 2 / 3 / 4+ different items.</div>
        </div>
        <button onClick={toggleBundle} className={`k-btn text-xs ${me.bundle_discount_enabled ? "primary" : ""}`} data-testid="bundle-toggle-btn">
          {me.bundle_discount_enabled ? <><Check size={14} /> ENABLED</> : <>ENABLE BULK DISCOUNTS</>}
        </button>
      </div>
      <form onSubmit={add} className="border border-white/10 bg-[#111318] p-6 mb-8 grid md:grid-cols-2 gap-3">
        <input placeholder="Product name" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} required className="k-input" data-testid="prod-name" />
        <input placeholder="Price (USD)" type="number" step="0.01" value={f.price} onChange={e => setF({ ...f, price: e.target.value })} required className="k-input" data-testid="prod-price" />
        <div className="md:col-span-2 border border-dashed border-white/15 p-4 bg-[#0A0D14]">
          <div className="text-[10px] font-mono-k tracking-widest-k text-[#FACC15] mb-2">— HERO IMAGE</div>
          {f.image ? (
            <div className="flex items-start gap-3 mb-2">
              <img src={f.image} alt="preview" className="w-24 h-24 object-cover border border-white/10" data-testid="prod-image-preview" />
              <button type="button" onClick={() => setF({ ...f, image: "" })} className="k-btn text-xs" data-testid="prod-image-clear"><X size={12} /> REMOVE</button>
            </div>
          ) : null}
          <div className="flex flex-col md:flex-row gap-2">
            <input placeholder="Paste image URL…" value={f.image?.startsWith("data:") ? "" : f.image} onChange={e => setF({ ...f, image: e.target.value })} className="k-input flex-1" data-testid="prod-image" />
            <label className={`k-btn text-xs cursor-pointer ${uploading ? "opacity-60" : ""}`} data-testid="prod-image-upload">
              <UploadSimple size={14} /> {uploading ? "UPLOADING…" : "UPLOAD IMAGE"}
              <input type="file" accept="image/*" onChange={upload} disabled={uploading} className="hidden" />
            </label>
          </div>
          <div className="text-[10px] font-mono-k text-[#A1A1AA] mt-2">JPEG / PNG / WEBP · max 3MB · stored inline.</div>
        </div>
        <textarea placeholder="Description" value={f.description} onChange={e => setF({ ...f, description: e.target.value })} className="k-input md:col-span-2" rows={2} data-testid="prod-desc" />
        <label className="flex items-start gap-2 md:col-span-2 border border-white/10 p-3 bg-[#0A0D14] cursor-pointer" data-testid="prod-requires-shipping">
          <input type="checkbox" checked={f.requires_shipping} onChange={e => setF({ ...f, requires_shipping: e.target.checked })} className="mt-1 w-4 h-4 accent-[#FACC15]" />
          <div>
            <div className="text-xs font-mono-k">REQUIRES SHIPPING</div>
            <div className="text-[10px] font-mono-k text-[#A1A1AA]">Tick this for physical items. Buyers will be asked for a full shipping address before payment.</div>
          </div>
        </label>
        <div className="md:col-span-2 border-t border-white/10 pt-3 mt-2">
          <div className="text-xs font-mono-k tracking-widest-k text-[#FACC15] mb-2">— DELIVERY (SHOWN AFTER PURCHASE)</div>
        </div>
        <input placeholder="Delivery URL — file, Notion, download link…" value={f.delivery_url} onChange={e => setF({ ...f, delivery_url: e.target.value })} className="k-input md:col-span-2" data-testid="prod-delivery-url" />
        <textarea placeholder="Delivery instructions — steps, thank-you message, or how you'll ship / follow up" value={f.delivery_note} onChange={e => setF({ ...f, delivery_note: e.target.value })} className="k-input md:col-span-2" rows={3} data-testid="prod-delivery-note" />
        <textarea placeholder="License key pool — one key per line (optional). Each purchase pops one." value={keysRaw} onChange={e => setKeysRaw(e.target.value)} className="k-input md:col-span-2 font-mono-k" rows={3} data-testid="prod-license-keys" />
        <button className="k-btn primary md:col-span-2 justify-center" data-testid="prod-add"><Plus size={16} /> ADD PRODUCT</button>
      </form>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-l border-white/10">
        {items.length === 0 && <div className="border-r border-b border-white/10 p-6 text-[#A1A1AA] font-mono-k text-sm">No products yet.</div>}
        {items.map(p => (
          <div key={p.id} className="border-r border-b border-white/10 p-4 bg-[#0A0D14]" data-testid={`prod-${p.id}`}>
            {p.image ? <img src={p.image} alt="" className="w-full h-40 object-cover mb-3 border border-white/10" /> : <div className="w-full h-40 bg-[#111318] border border-white/10 mb-3 flex items-center justify-center"><Storefront size={40} className="text-[#A1A1AA]" /></div>}
            <div className="font-display text-xl mb-1">{p.name}</div>
            <div className="text-xs font-mono-k text-[#A1A1AA] line-clamp-2 mb-3">{p.description}</div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[#FACC15] font-mono-k">${p.price}</div>
              <div className="text-xs font-mono-k text-[#A1A1AA]">{p.sales || 0} sold</div>
            </div>
            {(p.delivery_url || (p.license_keys && p.license_keys.length > 0)) && (
              <div className="text-[10px] font-mono-k tracking-widest-k text-[#FACC15] mb-2">
                DELIVERY: {p.delivery_url ? "URL" : ""}{p.delivery_url && p.license_keys?.length ? " + " : ""}{p.license_keys?.length ? `${p.license_keys.length} KEYS` : ""}
              </div>
            )}
            <button onClick={() => del(p.id)} className="k-btn text-xs w-full justify-center" data-testid={`prod-del-${p.id}`}><Trash size={14} /> DELETE</button>
          </div>
        ))}
      </div>
    </div>
  );
};

// -------- Leads --------
const LEAD_STATUSES = [
  { id: "new", label: "NEW", color: "#FACC15" },
  { id: "replied", label: "REPLIED", color: "#22C55E" },
  { id: "converted", label: "CONVERTED", color: "#00E5FF" },
  { id: "archived", label: "ARCHIVED", color: "#A1A1AA" },
];

const Leads = () => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");
  const [prefs, setPrefs] = useState({ ai_autoreply_enabled: false, ai_autoreply_intro: "" });
  const [dripEnabled, setDripEnabled] = useState(false);
  const [usage, setUsage] = useState({ used: 0, cap: 0, remaining: 0 });
  const [aiLoading, setAiLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const load = () => client.get(`/leads?status=${filter}`).then(r => setItems(r.data));
  const loadUsage = () => client.get("/leads/ai-usage").then(r => setUsage(r.data)).catch(() => {});
  useEffect(() => { load(); }, [filter]);
  useEffect(() => {
    client.get("/leads/prefs").then(r => setPrefs(r.data)).catch(() => {});
    client.get("/drip/settings").then(r => setDripEnabled(!!r.data?.drip_enabled)).catch(() => {});
    loadUsage();
  }, []);

  // Poll every 10s so new leads appear in real time
  useEffect(() => {
    const id = setInterval(() => { load(); loadUsage(); }, 10000);
    return () => clearInterval(id);
  }, [filter]);

  const savePrefs = async (next) => {
    setPrefs(next);
    try { await client.put("/leads/prefs", next); toast.success("Auto-reply preferences saved"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const saveDrip = async (val) => {
    try {
      await client.put("/drip/settings", { drip_enabled: val });
      setDripEnabled(val);
      toast.success(val ? "Drip enabled — day 2 + day 5 follow-ups will fire automatically" : "Drip sequences paused");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const openLead = async (id) => {
    const { data } = await client.get(`/leads/${id}`);
    setActive(data);
    setReply("");
  };

  const sendReply = async () => {
    if (!reply.trim()) return toast.error("Write a reply first");
    setSending(true);
    try {
      await client.post(`/leads/${active.id}/reply`, { body: reply });
      toast.success("Reply sent");
      setReply("");
      const { data } = await client.get(`/leads/${active.id}`);
      setActive(data);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    setSending(false);
  };

  const aiDraft = async () => {
    setAiLoading(true);
    try {
      const { data } = await client.post(`/leads/${active.id}/ai-reply`);
      setReply(data.draft);
      if (data.quota) setUsage(u => ({ ...u, used: data.quota.used, cap: data.quota.cap, remaining: data.quota.remaining }));
      toast.success("AI drafted your reply — edit or send");
    } catch (err) { toast.error(err?.response?.data?.detail || "AI failed"); }
    setAiLoading(false);
  };

  const setStatus = async (id, status) => {
    try {
      await client.put(`/leads/${id}/status`, { status });
      toast.success(`Marked ${status}`);
      load();
      if (active?.id === id) setActive({ ...active, status });
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const counts = LEAD_STATUSES.reduce((acc, s) => { acc[s.id] = items.filter(x => (x.status || "new") === s.id).length; return acc; }, {});
  const statusColor = (s) => LEAD_STATUSES.find(x => x.id === (s || "new"))?.color || "#A1A1AA";

  return (
    <div>
      <PageHead eyebrow="LEADS" title="Every submission. One inbox. Reply in one tap." />

      <div className="border border-[#FACC15]/40 bg-[#FACC15]/5 p-5 mb-6" data-testid="lead-ai-panel">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="flex-1 min-w-[240px]">
            <div className="flex items-center gap-2 mb-1">
              <div className="text-xs font-mono-k tracking-widest-k text-[#FACC15]">— AI AUTO-REPLY</div>
              {usage.cap > 0 && (
                <div className="text-[10px] font-mono-k tracking-widest-k px-2 py-0.5 border border-[#FACC15]/40 text-[#FACC15]" data-testid="ai-usage-badge">
                  {usage.remaining} / {usage.cap} LEFT TODAY
                </div>
              )}
            </div>
            <div className="font-display text-2xl mb-2">Never let a lead go cold.</div>
            <p className="text-xs font-mono-k text-[#A1A1AA] mb-3">When enabled, every new lead gets a personalised AI reply within seconds — up to {usage.cap || "your daily"} replies per day. You can still send your own follow-up any time.</p>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={prefs.ai_autoreply_enabled} onChange={e => savePrefs({ ...prefs, ai_autoreply_enabled: e.target.checked })} className="w-4 h-4 accent-[#FACC15]" data-testid="ai-autoreply-toggle" />
              <span className="text-sm font-mono-k">{prefs.ai_autoreply_enabled ? "ON — replies fire automatically" : "OFF — replies only when you hit send"}</span>
            </label>
            <textarea placeholder="Optional: tell the AI how you want to sound (tone, offer, next step)…" value={prefs.ai_autoreply_intro || ""} onChange={e => setPrefs({ ...prefs, ai_autoreply_intro: e.target.value })} onBlur={() => savePrefs(prefs)} rows={2} className="k-input mt-3 w-full text-sm" data-testid="ai-autoreply-intro" />
          </div>
        </div>
      </div>

      {/* Drip Email Sequences */}
      <div className="border border-white/10 bg-[#111318] p-5 mb-6" data-testid="drip-panel">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="flex-1 min-w-[240px]">
            <div className="text-xs font-mono-k tracking-widest-k text-[#FACC15] mb-1">— DRIP SEQUENCES</div>
            <div className="font-display text-2xl mb-2">Follow up on auto-pilot.</div>
            <p className="text-xs font-mono-k text-[#A1A1AA] mb-3">Every new lead gets a <strong className="text-white">day-2 check-in</strong> and a <strong className="text-white">day-5 last-call</strong> email signed from you — only if they haven't replied or been marked won/lost. Branded from <code className="text-[#FACC15]">no-reply@korporatelinks.com</code> with replies routed to your support inbox.</p>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={dripEnabled}
                onChange={e => saveDrip(e.target.checked)}
                className="w-4 h-4 accent-[#FACC15]"
                data-testid="drip-toggle"
              />
              <span className="text-sm font-mono-k">{dripEnabled ? "ON — day 2 + day 5 follow-ups fire automatically" : "OFF — no automatic follow-ups"}</span>
            </label>
          </div>
        </div>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        <button onClick={() => setFilter("all")} className={`k-btn text-xs ${filter === "all" ? "primary" : ""}`} data-testid="leads-filter-all">ALL · {items.length}</button>
        {LEAD_STATUSES.map(s => (
          <button key={s.id} onClick={() => setFilter(s.id)} className={`k-btn text-xs ${filter === s.id ? "primary" : ""}`} data-testid={`leads-filter-${s.id}`}>
            {s.label} · {counts[s.id] || 0}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-[1fr_1fr] gap-4">
        <div className="border border-white/10">
          {items.length === 0 ? (
            <div className="p-8 text-center text-sm font-mono-k text-[#A1A1AA]">No leads {filter !== "all" ? "in this bucket" : "yet"} — share your public page to collect some.</div>
          ) : items.map(l => (
            <button key={l.id} onClick={() => openLead(l.id)} className={`w-full text-left border-b border-white/10 p-4 hover:bg-[#111318] transition-colors ${active?.id === l.id ? "bg-[#111318]" : ""}`} data-testid={`lead-row-${l.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-display text-base tracking-tight truncate">{l.name}</div>
                  <div className="text-xs font-mono-k text-[#FACC15] truncate">{l.email}</div>
                  <div className="text-xs font-mono-k text-[#A1A1AA] mt-1 line-clamp-1">{l.message || "(no message)"}</div>
                </div>
                <div className="text-[9px] font-mono-k tracking-widest-k px-2 py-0.5 border" style={{ borderColor: statusColor(l.status), color: statusColor(l.status) }}>{(l.status || "new").toUpperCase()}</div>
              </div>
              <div className="text-[10px] font-mono-k text-[#A1A1AA] mt-2">{(l.created_at || "").slice(0, 16).replace("T", " ")}{l.ai_autoreplied ? " · AI ✨" : ""}</div>
            </button>
          ))}
        </div>

        <div className="border border-white/10 bg-[#111318] p-5 min-h-[400px]" data-testid="lead-detail">
          {!active ? (
            <div className="text-sm font-mono-k text-[#A1A1AA] text-center pt-12">← Pick a lead to reply.</div>
          ) : (
            <>
              <div className="flex items-start justify-between gap-2 mb-4">
                <div>
                  <div className="text-xs font-mono-k tracking-widest-k" style={{ color: statusColor(active.status) }}>— {(active.status || "NEW").toUpperCase()}</div>
                  <div className="font-display text-2xl mt-1">{active.name}</div>
                  <div className="text-xs font-mono-k text-[#FACC15]">{active.email}</div>
                  {active.phone && <div className="text-xs font-mono-k text-[#A1A1AA]">{active.phone}</div>}
                </div>
                <div className="flex gap-1 flex-wrap">
                  {LEAD_STATUSES.map(s => (
                    <button key={s.id} onClick={() => setStatus(active.id, s.id)} className={`k-btn text-[10px] px-2 py-1 ${active.status === s.id ? "primary" : ""}`} data-testid={`lead-set-${s.id}`} title={s.label}>{s.label}</button>
                  ))}
                </div>
              </div>

              <div className="border border-white/10 p-3 mb-4 bg-[#0A0D14]">
                <div className="text-[10px] font-mono-k tracking-widest-k text-[#A1A1AA] mb-1">ORIGINAL MESSAGE · {(active.created_at || "").slice(0, 16).replace("T", " ")}</div>
                <div className="text-sm whitespace-pre-wrap">{active.message || "(no message)"}</div>
              </div>

              {(active.replies || []).length > 0 && (
                <div className="mb-4">
                  <div className="text-[10px] font-mono-k tracking-widest-k text-[#FACC15] mb-2">— YOUR REPLIES</div>
                  {(active.replies || []).map((r, i) => (
                    <div key={i} className="border-l-2 border-[#FACC15] pl-3 mb-3">
                      <div className="text-[10px] font-mono-k text-[#A1A1AA] mb-1">
                        {r.from === "ai" ? "AI ✨" : r.operator_name} · {(r.created_at || "").slice(0, 16).replace("T", " ")}{r.sent_email ? " · email sent" : ""}
                      </div>
                      <div className="text-sm whitespace-pre-wrap">{r.body}</div>
                    </div>
                  ))}
                </div>
              )}

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-mono-k tracking-widest-k text-[#FACC15]">— WRITE A REPLY</div>
                  <button onClick={aiDraft} disabled={aiLoading} className="k-btn text-xs" data-testid="lead-ai-draft">
                    {aiLoading ? "AI THINKING…" : "AI DRAFT ✨"}
                  </button>
                </div>
                <textarea rows={6} value={reply} onChange={e => setReply(e.target.value)} placeholder="Write your reply — the AI Draft button can start it for you." className="k-input w-full text-sm" data-testid="lead-reply-input" />
                <button onClick={sendReply} disabled={sending || !reply.trim()} className="k-btn primary w-full justify-center" data-testid="lead-reply-send">
                  <EnvelopeSimple size={14} /> {sending ? "SENDING…" : "SEND REPLY BY EMAIL"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// -------- Funnels --------
const STEP_TYPES = [
  { id: "landing", label: "LANDING", desc: "Hook + CTA" },
  { id: "lead-form", label: "LEAD FORM", desc: "Capture name + email" },
  { id: "offer", label: "OFFER", desc: "Pitch a product or link" },
  { id: "thank-you", label: "THANK YOU", desc: "Wrap it up" },
];
const stepKey = () => `k${Math.random().toString(36).slice(2, 10)}`;
const emptyStep = (order) => ({ _key: stepKey(), order, type: "landing", title: "", subtitle: "", cta_label: "Continue", cta_url: "", image: "", form_fields: [], variant_b_title: "", variant_b_subtitle: "" });
const emptyFunnel = () => ({ id: null, name: "", kind: "lead", slug: "", goal: "", active: true, steps: [emptyStep(0)] });

const Funnels = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null = list, object = editor
  const [saving, setSaving] = useState(false);
  const [planInfo, setPlanInfo] = useState(null);
  const [statsFunnel, setStatsFunnel] = useState(null);

  const load = () => client.get("/funnels").then(r => setItems(r.data));
  const loadPlan = () => client.get("/plan/limits").then(r => setPlanInfo(r.data)).catch(() => {});
  useEffect(() => { load(); loadPlan(); }, []);

  const [templates, setTemplates] = useState([]);
  useEffect(() => { client.get("/funnel-templates").then(r => setTemplates(r.data)).catch(() => {}); }, []);

  const perKindCap = planInfo?.limits?.funnels_per_kind || null;
  const usedByKind = planInfo?.used?.funnels_by_kind || { lead: 0, sales: 0 };
  const isLeadCapped = !!perKindCap && (usedByKind.lead || 0) >= perKindCap;
  const isSalesCapped = !!perKindCap && (usedByKind.sales || 0) >= perKindCap;
  const kindLabel = (k) => k === "sales" ? "SALES" : "LEAD";

  const startNew = () => {
    // Free tier: default to whichever kind still has capacity; block if both capped
    if (perKindCap && isLeadCapped && isSalesCapped) {
      toast.error("You've used both free funnels. Upgrade to Pro for up to 10.");
      return;
    }
    const defaultKind = perKindCap && isLeadCapped ? "sales" : "lead";
    setEditing({ ...emptyFunnel(), kind: defaultKind });
  };
  const startEdit = (f) => setEditing({
    ...f,
    steps: (f.steps || []).length
      ? [...f.steps].sort((a, b) => a.order - b.order).map(s => ({ ...s, _key: s._key || stepKey() }))
      : [emptyStep(0)],
  });
  const del = async (id) => {
    if (!window.confirm("Delete this funnel? This cannot be undone.")) return;
    await client.delete(`/funnels/${id}`); load(); loadPlan(); toast.success("Funnel deleted");
  };

  const save = async () => {
    if (!editing.name.trim()) return toast.error("Name your funnel");
    if (!editing.steps.length) return toast.error("Add at least one step");
    if (editing.steps.some(s => !s.title.trim())) return toast.error("Every step needs a title");
    setSaving(true);
    try {
      const payload = {
        name: editing.name,
        kind: editing.kind,
        slug: editing.slug || "",
        goal: editing.goal || "",
        active: editing.active !== false,
        steps: editing.steps.map((s, i) => {
          const clean = { ...s, order: i };
          delete clean._key;
          return clean;
        }),
      };
      if (editing.id) await client.put(`/funnels/${editing.id}`, payload);
      else await client.post("/funnels", payload);
      toast.success("Funnel saved");
      setEditing(null);
      load(); loadPlan();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  const updateStep = (idx, patch) => setEditing(v => ({ ...v, steps: v.steps.map((s, i) => i === idx ? { ...s, ...patch } : s) }));
  const addStep = () => setEditing(v => ({ ...v, steps: [...v.steps, emptyStep(v.steps.length)] }));
  const removeStep = (idx) => setEditing(v => ({ ...v, steps: v.steps.filter((_, i) => i !== idx) }));
  const moveStep = (idx, dir) => setEditing(v => {
    const arr = [...v.steps];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return v;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    return { ...v, steps: arr };
  });
  const toggleFormField = (idx, field) => setEditing(v => ({
    ...v,
    steps: v.steps.map((s, i) => i === idx ? { ...s, form_fields: (s.form_fields || []).includes(field) ? s.form_fields.filter(x => x !== field) : [...(s.form_fields || []), field] } : s),
  }));

  if (editing) {
    return (
      <div>
        <PageHead eyebrow={editing.id ? "EDIT FUNNEL" : "NEW FUNNEL"} title="Design a multi-step journey — hook, capture, close." />

        <div className="border border-white/10 bg-[#111318] p-6 mb-6 grid md:grid-cols-2 gap-3">
          <input placeholder="Funnel name (e.g. 'Free Guide Giveaway')" value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} className="k-input" data-testid="funnel-name" />
          <select value={editing.kind} onChange={e => setEditing({ ...editing, kind: e.target.value })} className="k-input" data-testid="funnel-kind">
            <option value="lead" disabled={perKindCap && isLeadCapped && editing.kind !== "lead"}>LEAD FUNNEL — Capture emails / phones{perKindCap && isLeadCapped && editing.kind !== "lead" ? " · CAPPED" : ""}</option>
            <option value="sales" disabled={perKindCap && isSalesCapped && editing.kind !== "sales"}>SALES FUNNEL — Push to a checkout{perKindCap && isSalesCapped && editing.kind !== "sales" ? " · CAPPED" : ""}</option>
          </select>
          <input placeholder="URL slug (auto-generated if blank)" value={editing.slug} onChange={e => setEditing({ ...editing, slug: e.target.value })} className="k-input" data-testid="funnel-slug" />
          <input placeholder="Goal (e.g. 'collect 100 emails')" value={editing.goal} onChange={e => setEditing({ ...editing, goal: e.target.value })} className="k-input" data-testid="funnel-goal" />
          <label className="flex items-center gap-2 md:col-span-2 text-sm font-mono-k">
            <input type="checkbox" checked={editing.active !== false} onChange={e => setEditing({ ...editing, active: e.target.checked })} className="w-4 h-4 accent-[#FACC15]" data-testid="funnel-active" />
            <span>{editing.active !== false ? "ACTIVE — publicly accessible at /f/<slug>" : "PAUSED — will 404 for visitors"}</span>
          </label>
        </div>

        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs font-mono-k tracking-widest-k text-[#FACC15]">— STEPS · {editing.steps.length}</div>
            <button onClick={addStep} className="k-btn text-xs" data-testid="funnel-add-step"><Plus size={12} /> ADD STEP</button>
          </div>
          <div className="space-y-3">
            {editing.steps.map((s, idx) => (
              <div key={s._key || idx} className="border border-white/10 bg-[#0A0D14] p-4" data-testid={`funnel-step-${idx}`}>
                <div className="flex items-start gap-2 mb-3">
                  <div className="text-[10px] font-mono-k tracking-widest-k text-[#FACC15] shrink-0">STEP {idx + 1}</div>
                  <select value={s.type} onChange={e => updateStep(idx, { type: e.target.value })} className="k-input text-xs flex-1" data-testid={`funnel-step-type-${idx}`}>
                    {STEP_TYPES.map(t => <option key={t.id} value={t.id}>{t.label} — {t.desc}</option>)}
                  </select>
                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => moveStep(idx, -1)} disabled={idx === 0} className="k-btn text-xs px-2 py-1 disabled:opacity-30" data-testid={`funnel-step-up-${idx}`}><CaretUp size={12} /></button>
                    <button onClick={() => moveStep(idx, 1)} disabled={idx === editing.steps.length - 1} className="k-btn text-xs px-2 py-1 disabled:opacity-30" data-testid={`funnel-step-down-${idx}`}><CaretDown size={12} /></button>
                    <button onClick={() => removeStep(idx)} disabled={editing.steps.length <= 1} className="k-btn text-xs px-2 py-1 disabled:opacity-30" data-testid={`funnel-step-del-${idx}`}><Trash size={12} /></button>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-2">
                  <input placeholder="Headline / title" value={s.title} onChange={e => updateStep(idx, { title: e.target.value })} className="k-input md:col-span-2" data-testid={`funnel-step-title-${idx}`} />
                  <input placeholder="Subtitle / body copy" value={s.subtitle || ""} onChange={e => updateStep(idx, { subtitle: e.target.value })} className="k-input md:col-span-2" data-testid={`funnel-step-subtitle-${idx}`} />
                  {s.type !== "thank-you" && (
                    <input placeholder="CTA label (e.g. 'CLAIM MY GUIDE')" value={s.cta_label || ""} onChange={e => updateStep(idx, { cta_label: e.target.value })} className="k-input" data-testid={`funnel-step-cta-${idx}`} />
                  )}
                  {(s.type === "offer" || s.type === "landing") && (
                    <input placeholder="CTA URL (external link, optional)" value={s.cta_url || ""} onChange={e => updateStep(idx, { cta_url: e.target.value })} className="k-input" data-testid={`funnel-step-cta-url-${idx}`} />
                  )}
                  {(s.type === "landing" || s.type === "offer") && (
                    <div className="md:col-span-2 border border-dashed border-white/15 p-3 bg-[#111318]">
                      <div className="text-[10px] font-mono-k tracking-widest-k text-[#00E5FF] mb-2">— VARIANT B (A/B TEST) · leave blank to skip test</div>
                      <div className="grid md:grid-cols-2 gap-2">
                        <input placeholder="Alternate headline" value={s.variant_b_title || ""} onChange={e => updateStep(idx, { variant_b_title: e.target.value })} className="k-input" data-testid={`funnel-step-vb-title-${idx}`} />
                        <input placeholder="Alternate subtitle" value={s.variant_b_subtitle || ""} onChange={e => updateStep(idx, { variant_b_subtitle: e.target.value })} className="k-input" data-testid={`funnel-step-vb-subtitle-${idx}`} />
                      </div>
                      <div className="text-[10px] font-mono-k text-[#A1A1AA] mt-2">Visitors are split 50 / 50. Once each variant has 50+ views and one is 20% ahead in conversion rate, we auto-promote the winner. You can also lock a winner manually from the funnel list.</div>
                    </div>
                  )}
                  {s.type === "lead-form" && (
                    <div className="md:col-span-2 flex gap-3 flex-wrap items-center">
                      <div className="text-[10px] font-mono-k tracking-widest-k text-[#A1A1AA]">FIELDS:</div>
                      {["name", "email", "phone", "message"].map(field => (
                        <label key={field} className="flex items-center gap-1 text-xs font-mono-k cursor-pointer">
                          <input type="checkbox" checked={(s.form_fields || ["name", "email"]).includes(field)} onChange={() => toggleFormField(idx, field)} className="accent-[#FACC15]" data-testid={`funnel-step-${idx}-field-${field}`} />
                          <span className="uppercase">{field}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex gap-2 flex-wrap">
          <button onClick={save} disabled={saving} className="k-btn primary" data-testid="funnel-save"><Check size={14} /> {saving ? "SAVING…" : "SAVE FUNNEL"}</button>
          <button onClick={() => setEditing(null)} className="k-btn" data-testid="funnel-cancel"><X size={14} /> CANCEL</button>
        </div>
      </div>
    );
  }

  const spawnFromTemplate = async (tpl) => {
    const kindCapped = (tpl.kind === "sales" ? isSalesCapped : isLeadCapped);
    if (kindCapped) {
      toast.error(`Free plan is limited to 1 ${kindLabel(tpl.kind).toLowerCase()} funnel. Upgrade to Pro to add more.`);
      return;
    }
    try {
      const { data } = await client.post("/funnels/from-template", { template_id: tpl.id });
      toast.success(`"${tpl.name}" spawned — customize any step`);
      load(); loadPlan();
      // Open editor pre-filled
      startEdit(data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <PageHead eyebrow="FUNNELS" title="Multi-step journeys that turn visitors into leads." />

      {perKindCap && (
        <div className="mb-6 border border-white/10 bg-[#111318] p-4 flex flex-wrap items-center justify-between gap-3" data-testid="funnel-plan-banner">
          <div className="flex items-center gap-4">
            <div className="text-[10px] font-mono-k tracking-widest-k text-[#FACC15]">— FREE PLAN LIMITS</div>
            <div className="flex items-center gap-3">
              <div className={`px-3 py-1 border font-mono-k text-xs ${isLeadCapped ? "border-red-400/40 bg-red-400/5 text-red-300" : "border-[#FACC15]/40 bg-[#FACC15]/5 text-[#FACC15]"}`} data-testid="funnel-cap-lead">
                LEAD · {usedByKind.lead || 0}/{perKindCap}
              </div>
              <div className={`px-3 py-1 border font-mono-k text-xs ${isSalesCapped ? "border-red-400/40 bg-red-400/5 text-red-300" : "border-[#FACC15]/40 bg-[#FACC15]/5 text-[#FACC15]"}`} data-testid="funnel-cap-sales">
                SALES · {usedByKind.sales || 0}/{perKindCap}
              </div>
            </div>
          </div>
          <Link to="/dashboard/pricing" className="k-btn text-xs" data-testid="funnel-upgrade-cta">
            <Sparkle size={12} /> UPGRADE TO PRO — UNLOCK 10 FUNNELS
          </Link>
        </div>
      )}

      {/* Template gallery */}
      {templates.length > 0 && (
        <div className="mb-8" data-testid="funnel-template-gallery">
          <div className="text-xs font-mono-k tracking-widest-k text-[#FACC15] mb-2">— START FROM A TEMPLATE</div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-l border-white/10">
            {templates.map(t => {
              const capped = t.kind === "sales" ? isSalesCapped : isLeadCapped;
              return (
                <button
                  key={t.id}
                  onClick={() => spawnFromTemplate(t)}
                  disabled={capped}
                  title={capped ? `Free plan limit — 1 ${kindLabel(t.kind).toLowerCase()} funnel` : ""}
                  className={`text-left border-r border-b border-white/10 p-4 transition-colors group ${capped ? "bg-[#0A0D14] opacity-40 cursor-not-allowed" : "bg-[#111318] hover:bg-[#FACC15]/5 hover:border-[#FACC15]/40"}`}
                  data-testid={`funnel-tpl-${t.id}`}
                >
                  <div className="text-[10px] font-mono-k tracking-widest-k text-[#FACC15] mb-1">— {t.eyebrow || t.kind.toUpperCase()}{capped ? " · LOCKED" : ""}</div>
                  <div className="font-display text-lg mb-1 group-hover:text-[#FACC15] transition-colors">{t.name}</div>
                  <div className="text-xs font-mono-k text-[#A1A1AA] mb-2">{t.goal}</div>
                  <div className="text-[10px] font-mono-k text-white/50">{t.steps.length} steps · {t.steps.map(s => s.type.replace("-", " ")).join(" → ")}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <p className="text-xs font-mono-k text-[#A1A1AA] max-w-xl">Or build one from scratch — each funnel gets a shareable `/f/&lt;slug&gt;` link and tracks views, leads, and conversions.</p>
        <button
          onClick={startNew}
          disabled={perKindCap && isLeadCapped && isSalesCapped}
          className={`k-btn primary ${perKindCap && isLeadCapped && isSalesCapped ? "opacity-40 cursor-not-allowed" : ""}`}
          data-testid="funnel-new"
        >
          <Plus size={14} /> NEW FROM SCRATCH
        </button>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-l border-white/10">
        {items.length === 0 && (
          <div className="border-r border-b border-white/10 p-6 text-[#A1A1AA] font-mono-k text-sm md:col-span-2 lg:col-span-3">No funnels yet. Click NEW FUNNEL to build one.</div>
        )}
        {items.map(f => {
          const publicUrl = `${window.location.origin}/f/${f.slug}`;
          const conv = f.views > 0 ? Math.round(((f.leads_captured || 0) / f.views) * 100) : 0;
          const va = f.ab_views?.A || 0, vb = f.ab_views?.B || 0;
          const la = f.ab_leads?.A || 0, lb = f.ab_leads?.B || 0;
          const cra = va ? Math.round((la / va) * 100) : 0;
          const crb = vb ? Math.round((lb / vb) * 100) : 0;
          const hasAB = (f.steps || []).some(s => s.variant_b_title || s.variant_b_subtitle);
          const winner = f.ab_winner || null;
          const setWinner = async (w) => {
            try {
              await client.put(`/funnels/${f.id}/ab-winner`, { winner: w });
              toast.success(w ? `Locked winner: ${w}` : "Reset — split resumes");
              load();
            } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
          };
          return (
            <div key={f.id} className="border-r border-b border-white/10 p-5 bg-[#0A0D14]" data-testid={`funnel-row-${f.id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <div className="text-[10px] font-mono-k tracking-widest-k" style={{ color: f.active !== false ? "#FACC15" : "#A1A1AA" }}>— {f.kind?.toUpperCase() || "LEAD"} · {f.active !== false ? "LIVE" : "PAUSED"}</div>
                  <div className="font-display text-xl truncate">{f.name}</div>
                  <div className="text-[10px] font-mono-k text-[#A1A1AA] truncate">/f/{f.slug}</div>
                </div>
                <div className="text-[10px] font-mono-k tracking-widest-k text-[#FACC15] shrink-0">{(f.steps || []).length} STEPS</div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center my-3 border-y border-white/10 py-2">
                <div><div className="text-lg font-display text-white">{f.views || 0}</div><div className="text-[9px] font-mono-k text-[#A1A1AA] tracking-widest-k">VIEWS</div></div>
                <div><div className="text-lg font-display text-[#FACC15]">{f.leads_captured || 0}</div><div className="text-[9px] font-mono-k text-[#A1A1AA] tracking-widest-k">LEADS</div></div>
                <div><div className="text-lg font-display text-[#22C55E]">{conv}%</div><div className="text-[9px] font-mono-k text-[#A1A1AA] tracking-widest-k">CONV</div></div>
              </div>
              {hasAB && (
                <div className="border border-[#00E5FF]/30 bg-[#00E5FF]/5 p-2 mb-3" data-testid={`funnel-ab-${f.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-[9px] font-mono-k tracking-widest-k text-[#00E5FF]">— A/B TEST</div>
                    {winner ? (
                      <button onClick={() => setWinner("")} className="text-[9px] font-mono-k text-[#A1A1AA] hover:text-white" data-testid={`funnel-ab-reset-${f.id}`}>RESET</button>
                    ) : (
                      <div className="text-[9px] font-mono-k text-[#A1A1AA]">50/50 SPLIT</div>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <button onClick={() => !winner && setWinner("A")} disabled={!!winner} className={`p-2 border text-left transition-colors ${winner === "A" ? "border-[#22C55E] bg-[#22C55E]/10" : "border-white/10 hover:border-[#00E5FF]/50"}`} data-testid={`funnel-ab-pick-a-${f.id}`}>
                      <div className="text-[9px] font-mono-k tracking-widest-k" style={{ color: winner === "A" ? "#22C55E" : "#00E5FF" }}>VARIANT A {winner === "A" ? "· WINNER" : ""}</div>
                      <div className="text-xs font-mono-k mt-1">{va} views · {la} leads · <span className="text-[#FACC15]">{cra}%</span></div>
                    </button>
                    <button onClick={() => !winner && setWinner("B")} disabled={!!winner} className={`p-2 border text-left transition-colors ${winner === "B" ? "border-[#22C55E] bg-[#22C55E]/10" : "border-white/10 hover:border-[#00E5FF]/50"}`} data-testid={`funnel-ab-pick-b-${f.id}`}>
                      <div className="text-[9px] font-mono-k tracking-widest-k" style={{ color: winner === "B" ? "#22C55E" : "#00E5FF" }}>VARIANT B {winner === "B" ? "· WINNER" : ""}</div>
                      <div className="text-xs font-mono-k mt-1">{vb} views · {lb} leads · <span className="text-[#FACC15]">{crb}%</span></div>
                    </button>
                  </div>
                </div>
              )}
              <div className="flex gap-1 flex-wrap">
                <button onClick={() => startEdit(f)} className="k-btn text-xs flex-1 justify-center" data-testid={`funnel-edit-${f.id}`}><Gear size={12} /> EDIT</button>
                <button onClick={() => setStatsFunnel(f)} className="k-btn text-xs flex-1 justify-center" data-testid={`funnel-stats-${f.id}`}><ChartLineUp size={12} /> STATS</button>
                <a href={publicUrl} target="_blank" rel="noreferrer" className="k-btn text-xs flex-1 justify-center" data-testid={`funnel-view-${f.id}`}><Eye size={12} /> VIEW</a>
                <button onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success("Link copied"); }} className="k-btn text-xs flex-1 justify-center" data-testid={`funnel-copy-${f.id}`}><Copy size={12} /> COPY</button>
                <button onClick={() => del(f.id)} className="k-btn text-xs" data-testid={`funnel-del-${f.id}`}><Trash size={12} /></button>
              </div>
            </div>
          );
        })}
      </div>
      {statsFunnel && <FunnelStatsModal funnel={statsFunnel} onClose={() => setStatsFunnel(null)} />}
    </div>
  );
};

// -------- Campaigns --------
const CHANNELS = ["tiktok", "instagram", "linkedin", "youtube", "facebook", "google"];
const Campaigns = () => {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ name: "", channel: "tiktok", budget: 100, audience: "", keywords: [], status: "draft" });
  const [kw, setKw] = useState("");
  const load = () => client.get("/campaigns").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);
  const add = async (e) => {
    e.preventDefault();
    await client.post("/campaigns", { ...f, budget: parseFloat(f.budget), keywords: kw.split(",").map(s => s.trim()).filter(Boolean) });
    setF({ name: "", channel: "tiktok", budget: 100, audience: "", keywords: [], status: "draft" }); setKw(""); load();
    toast.success("Campaign launched");
  };
  const del = async (id) => { await client.delete(`/campaigns/${id}`); load(); };
  return (
    <div>
      <PageHead eyebrow="PAID CAMPAIGNS" title="Launch, measure, iterate." />
      <form onSubmit={add} className="border border-white/10 bg-[#111318] p-6 mb-8 grid md:grid-cols-2 gap-3">
        <input placeholder="Campaign name" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} required className="k-input" data-testid="camp-name" />
        <select value={f.channel} onChange={e => setF({ ...f, channel: e.target.value })} className="k-input" data-testid="camp-channel">
          {CHANNELS.map(c => <option key={c} value={c}>{c.toUpperCase()}</option>)}
        </select>
        <input placeholder="Budget (USD)" type="number" value={f.budget} onChange={e => setF({ ...f, budget: e.target.value })} required className="k-input" data-testid="camp-budget" />
        <input placeholder="Audience" value={f.audience} onChange={e => setF({ ...f, audience: e.target.value })} className="k-input" data-testid="camp-audience" />
        <input placeholder="Keywords (comma-separated)" value={kw} onChange={e => setKw(e.target.value)} className="k-input md:col-span-2" data-testid="camp-keywords" />
        <button className="k-btn primary md:col-span-2 justify-center" data-testid="camp-add"><Megaphone size={16} /> LAUNCH</button>
      </form>
      <div className="border-t border-l border-white/10">
        {items.map(c => (
          <div key={c.id} className="border-r border-b border-white/10 p-4 grid md:grid-cols-6 gap-3 items-center" data-testid={`camp-${c.id}`}>
            <div>
              <div className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA]">{c.channel?.toUpperCase()}</div>
              <div className="font-medium truncate">{c.name}</div>
            </div>
            <div><div className="text-xs font-mono-k text-[#A1A1AA]">BUDGET</div><div>${c.budget}</div></div>
            <div><div className="text-xs font-mono-k text-[#A1A1AA]">SPENT</div><div className="text-[#FACC15]">${c.spent}</div></div>
            <div><div className="text-xs font-mono-k text-[#A1A1AA]">CLICKS</div><div>{c.clicks}</div></div>
            <div><div className="text-xs font-mono-k text-[#A1A1AA]">CONVERSIONS</div><div>{c.conversions}</div></div>
            <button onClick={() => del(c.id)} className="k-btn text-xs justify-self-end" data-testid={`camp-del-${c.id}`}><Trash size={14} /></button>
          </div>
        ))}
        {items.length === 0 && <div className="border-r border-b border-white/10 p-6 text-[#A1A1AA] font-mono-k text-sm">No campaigns yet.</div>}
      </div>
    </div>
  );
};

// -------- AI Copilot --------
const AI = () => {
  const [task, setTask] = useState("keyword_generation");
  const [prompt, setPrompt] = useState("");
  const [context, setContext] = useState("");
  const [out, setOut] = useState("");
  const [loading, setLoading] = useState(false);
  const tasks = [
    { v: "keyword_generation", l: "KEYWORD GENERATION" },
    { v: "funnel_copy", l: "FUNNEL COPY" },
    { v: "campaign_ideas", l: "CAMPAIGN IDEAS" },
    { v: "chat", l: "FREE CHAT" },
  ];
  const run = async () => {
    if (!prompt.trim()) return;
    setLoading(true); setOut("");
    try {
      const { data } = await client.post("/ai/generate", { task, prompt, context });
      setOut(data.text);
    } catch (err) { toast.error(err?.response?.data?.detail || "AI failed"); }
    finally { setLoading(false); }
  };
  return (
    <div>
      <PageHead eyebrow="AI COPILOT" title="Claude 4.6. Marketing chief-of-staff." />
      <div className="grid lg:grid-cols-2 gap-0 border-t border-l border-white/10">
        <div className="border-r border-b border-white/10 p-6">
          <div className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA] mb-2">TASK</div>
          <div className="grid grid-cols-2 gap-2 mb-4">
            {tasks.map(t => (
              <button key={t.v} onClick={() => setTask(t.v)} className={`p-3 text-xs font-mono-k tracking-widest-k border ${task === t.v ? "border-[#FACC15] bg-[#FACC15] text-black" : "border-white/20 text-[#A1A1AA] hover:text-white"}`} data-testid={`ai-task-${t.v}`}>{t.l}</button>
            ))}
          </div>
          <label className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA]">PROMPT</label>
          <textarea value={prompt} onChange={e => setPrompt(e.target.value)} className="k-input mt-1" rows={4} placeholder="e.g. keywords for a vegan protein powder DTC brand" data-testid="ai-prompt" />
          <label className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA] mt-3 block">CONTEXT (OPTIONAL)</label>
          <textarea value={context} onChange={e => setContext(e.target.value)} className="k-input mt-1" rows={3} data-testid="ai-context" />
          <button disabled={loading} onClick={run} className="k-btn primary w-full justify-center mt-4" data-testid="ai-run">
            {loading ? "GENERATING…" : "GENERATE"} <PaperPlaneTilt size={16} />
          </button>
        </div>
        <div className="border-r border-b border-white/10 p-6 bg-[#111318]">
          <div className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA] mb-2">OUTPUT</div>
          <pre data-testid="ai-output" className="whitespace-pre-wrap font-mono-k text-sm text-white leading-relaxed min-h-[300px]">{out || "Output appears here…"}</pre>
        </div>
      </div>
    </div>
  );
};

// -------- Appearance / Theme --------
const FONTS = ["Clash Display", "Bebas Neue", "Playfair Display", "Space Grotesk", "Manrope", "Poppins", "Inter", "IBM Plex Sans", "IBM Plex Mono", "JetBrains Mono", "Lora", "Archivo Black", "DM Sans"];
const BG_PRESETS = [
  { id: "brutalist", label: "Brutalist Dark (default)", preview: "#0A0D14" },
  { id: "sunset", label: "Sunset Gradient", preview: "linear-gradient(135deg,#ff6a3d 0%,#ffb400 100%)", bg_type: "gradient", bg_gradient: "linear-gradient(135deg,#ff6a3d 0%,#ffb400 100%)" },
  { id: "ocean", label: "Ocean Gradient", preview: "linear-gradient(135deg,#0f2027 0%,#2c5364 100%)", bg_type: "gradient", bg_gradient: "linear-gradient(135deg,#0f2027 0%,#203a43 50%,#2c5364 100%)" },
  { id: "aurora", label: "Aurora", preview: "linear-gradient(135deg,#00c9ff 0%,#92fe9d 100%)", bg_type: "gradient", bg_gradient: "linear-gradient(135deg,#00c9ff 0%,#92fe9d 100%)" },
  { id: "candy", label: "Candy Pop", preview: "linear-gradient(135deg,#ff9a9e 0%,#fad0c4 100%)", bg_type: "gradient", bg_gradient: "linear-gradient(135deg,#ff9a9e 0%,#fad0c4 100%)" },
  { id: "mono", label: "Mono Mist", preview: "linear-gradient(135deg,#232526 0%,#414345 100%)", bg_type: "gradient", bg_gradient: "linear-gradient(135deg,#232526 0%,#414345 100%)" },
  { id: "midnight", label: "Midnight Neon", preview: "linear-gradient(135deg,#0f0c29 0%,#302b63 50%,#24243e 100%)", bg_type: "gradient", bg_gradient: "linear-gradient(135deg,#0f0c29 0%,#302b63 50%,#24243e 100%)" },
  { id: "forest", label: "Forest", preview: "linear-gradient(135deg,#134e5e 0%,#71b280 100%)", bg_type: "gradient", bg_gradient: "linear-gradient(135deg,#134e5e 0%,#71b280 100%)" },
];
const ACCENT_SWATCHES = ["#FACC15", "#00E5FF", "#FF3D71", "#22C55E", "#A855F7", "#F97316", "#FFFFFF", "#EF4444"];
const CARD_STYLES = [
  { id: "brutal", label: "Brutal (sharp)" },
  { id: "rounded", label: "Rounded" },
  { id: "glass", label: "Glass (blur)" },
];

const Appearance = ({ user }) => {
  const [t, setT] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { client.get("/theme").then(r => setT(r.data)); }, []);
  const save = async (patch) => {
    const next = { ...t, ...patch };
    setT(next);
    setSaving(true);
    try {
      await client.put("/theme", patch);
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };
  const applyBgPreset = (p) => {
    if (p.id === "brutalist") save({ bg_type: "brutalist", bg_color: "#0A0D14", bg_gradient: "" });
    else save({ bg_type: p.bg_type, bg_gradient: p.bg_gradient });
  };
  if (!t) return <div className="text-[#A1A1AA] font-mono-k">Loading…</div>;

  const publicUrl = user?.slug ? `${window.location.origin}/u/${user.slug}` : "";

  return (
    <div>
      <PageHead eyebrow="APPEARANCE" title="Style your public bio." />
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="text-xs font-mono-k text-[#A1A1AA]">
          {saving ? "Saving…" : "Changes save automatically"} · Preview at{" "}
          {publicUrl && <a href={publicUrl} target="_blank" rel="noreferrer" className="text-[#FACC15] hover:underline" data-testid="theme-preview-link">{publicUrl.replace(/^https?:\/\//, "")}</a>}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-0 border-t border-l border-white/10">
        {/* Background presets */}
        <div className="border-r border-b border-white/10 p-6 bg-[#111318] lg:col-span-2">
          <div className="text-xs font-mono-k tracking-widest-k text-[#FACC15] mb-4">— BACKGROUND</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            {BG_PRESETS.map(p => {
              const active = (p.id === "brutalist" && t.bg_type === "brutalist") || t.bg_gradient === p.bg_gradient;
              return (
                <button key={p.id} onClick={() => applyBgPreset(p)} className={`border ${active ? "border-[#FACC15]" : "border-white/20"} p-1 hover:border-[#FACC15] transition-colors`} data-testid={`bg-preset-${p.id}`}>
                  <div className="w-full h-16" style={{ background: p.preview }} />
                  <div className="text-[10px] font-mono-k tracking-widest-k text-[#A1A1AA] mt-1 truncate">{p.label}</div>
                </button>
              );
            })}
          </div>
          <div className="grid md:grid-cols-2 gap-3 pt-3 border-t border-white/10">
            <div>
              <label className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA]">CUSTOM SOLID COLOR</label>
              <div className="flex items-center gap-2 mt-1">
                <input type="color" value={t.bg_color} onChange={e => save({ bg_type: "solid", bg_color: e.target.value })} className="w-12 h-10 bg-transparent border border-white/20" data-testid="bg-color-picker" />
                <input value={t.bg_color} onChange={e => save({ bg_type: "solid", bg_color: e.target.value })} className="k-input flex-1 font-mono-k text-xs" placeholder="#0A0D14" />
              </div>
            </div>
            <div>
              <label className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA]">CUSTOM IMAGE URL</label>
              <input value={t.bg_image} onChange={e => save({ bg_type: e.target.value ? "image" : t.bg_type, bg_image: e.target.value })} className="k-input mt-1" placeholder="https://…/hero.jpg" data-testid="bg-image-input" />
            </div>
          </div>
        </div>

        {/* Accent color */}
        <div className="border-r border-b border-white/10 p-6 bg-[#0A0D14]">
          <div className="text-xs font-mono-k tracking-widest-k text-[#FACC15] mb-4">— ACCENT COLOR</div>
          <div className="grid grid-cols-4 gap-2 mb-3">
            {ACCENT_SWATCHES.map(c => (
              <button key={c} onClick={() => save({ accent_color: c })} className={`h-12 border-2 ${t.accent_color?.toUpperCase() === c ? "border-white" : "border-white/20"} hover:border-white`} style={{ background: c }} data-testid={`accent-${c.slice(1)}`} />
            ))}
          </div>
          <div className="flex items-center gap-2">
            <input type="color" value={t.accent_color} onChange={e => save({ accent_color: e.target.value })} className="w-12 h-10 bg-transparent border border-white/20" data-testid="accent-picker" />
            <input value={t.accent_color} onChange={e => save({ accent_color: e.target.value })} className="k-input flex-1 font-mono-k text-xs" />
          </div>
        </div>

        {/* Fonts */}
        <div className="border-r border-b border-white/10 p-6 bg-[#0A0D14]">
          <div className="text-xs font-mono-k tracking-widest-k text-[#FACC15] mb-4">— TYPOGRAPHY</div>
          <div className="mb-4">
            <label className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA]">DISPLAY / HEADINGS</label>
            <select value={t.font_display} onChange={e => save({ font_display: e.target.value })} className="k-input mt-1 w-full" data-testid="font-display">
              {FONTS.map(f => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA]">BODY TEXT</label>
            <select value={t.font_body} onChange={e => save({ font_body: e.target.value })} className="k-input mt-1 w-full" data-testid="font-body">
              {FONTS.map(f => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}
            </select>
          </div>
        </div>

        {/* Card style */}
        <div className="border-r border-b border-white/10 p-6 bg-[#111318] lg:col-span-2">
          <div className="text-xs font-mono-k tracking-widest-k text-[#FACC15] mb-4">— LINK CARD STYLE</div>
          <div className="grid grid-cols-3 gap-3">
            {CARD_STYLES.map(cs => (
              <button key={cs.id} onClick={() => save({ card_style: cs.id })} className={`border p-4 text-left ${t.card_style === cs.id ? "border-[#FACC15]" : "border-white/20"} hover:border-[#FACC15]`} data-testid={`card-style-${cs.id}`}>
                <div className={`h-12 mb-2 bg-white/10 ${cs.id === "rounded" ? "rounded-2xl" : cs.id === "glass" ? "rounded-lg backdrop-blur-sm" : ""}`} />
                <div className="text-xs font-mono-k tracking-widest-k flex items-center gap-2">
                  {t.card_style === cs.id && <Check size={14} className="text-[#FACC15]" />} {cs.label}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// -------- Social --------
const PLATFORMS = [
  { id: "instagram", icon: InstagramLogo, name: "INSTAGRAM" },
  { id: "tiktok", icon: TiktokLogo, name: "TIKTOK" },
  { id: "youtube", icon: YoutubeLogo, name: "YOUTUBE" },
  { id: "linkedin", icon: LinkedinLogo, name: "LINKEDIN" },
  { id: "facebook", icon: FacebookLogo, name: "FACEBOOK" },
];

// -------- Ad Accounts (OAuth) --------
const AdAccounts = () => {
  const [cfg, setCfg] = useState(null);
  const [conn, setConn] = useState({ meta: { connected: false }, google_ads: { connected: false } });
  const [busy, setBusy] = useState({ meta: false, google_ads: false });
  const [metaAccounts, setMetaAccounts] = useState(null);
  const [googleAccounts, setGoogleAccounts] = useState(null);

  const loadAll = () => Promise.all([
    client.get("/oauth/config").then(r => setCfg(r.data)),
    client.get("/oauth/connections").then(r => setConn(r.data)),
  ]).catch(() => {});

  useEffect(() => {
    loadAll();
    // Detect return from OAuth callback
    const p = new URLSearchParams(window.location.search);
    if (p.get("meta") === "connected") toast.success("Facebook & Instagram ads connected");
    if (p.get("meta") === "denied") toast.error("Meta connection denied");
    if (p.get("google_ads") === "connected") toast.success("Google Ads connected");
    if (p.get("google_ads") === "denied") toast.error("Google Ads connection denied");
    if (p.get("meta") || p.get("google_ads")) {
      // Clean URL
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const connect = async (provider) => {
    setBusy(b => ({ ...b, [provider]: true }));
    try {
      const path = provider === "meta" ? "/oauth/meta/start" : "/oauth/google-ads/start";
      const { data } = await client.get(path);
      window.location.assign(data.authorization_url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Cannot start OAuth — not configured");
      setBusy(b => ({ ...b, [provider]: false }));
    }
  };

  const disconnect = async (provider) => {
    if (!window.confirm(`Disconnect ${provider === "meta" ? "Meta" : "Google Ads"}?`)) return;
    await client.delete(`/oauth/connections/${provider}`);
    toast.success("Disconnected");
    if (provider === "meta") setMetaAccounts(null);
    if (provider === "google_ads") setGoogleAccounts(null);
    loadAll();
  };

  const loadMetaAccounts = async () => {
    try {
      const { data } = await client.get("/meta/ad-accounts");
      setMetaAccounts(data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const loadGoogleAccounts = async () => {
    try {
      const { data } = await client.get("/google-ads/accounts");
      setGoogleAccounts(data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const ProviderCard = ({ id, name, brand, subtitle, connected_at, external_name, accounts, onLoad, configured, notReadyReason }) => (
    <div className={`border-r border-b border-white/10 p-6 bg-[#111318]`} data-testid={`adconn-${id}`}>
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 border flex items-center justify-center" style={{ borderColor: brand, background: brand }}>
          <Lightning weight="fill" size={20} color="#050505" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-xl truncate">{name}</div>
          <div className="text-[10px] font-mono-k tracking-widest-k text-[#A1A1AA]">{subtitle}</div>
        </div>
        <div className="text-[10px] font-mono-k tracking-widest-k px-2 py-1 border" style={{ borderColor: conn[id]?.connected ? "#22C55E" : "#A1A1AA", color: conn[id]?.connected ? "#22C55E" : "#A1A1AA" }} data-testid={`adconn-${id}-status`}>
          {conn[id]?.connected ? "LIVE" : "NOT CONNECTED"}
        </div>
      </div>

      {!configured ? (
        <div className="border border-red-500/40 bg-red-500/5 p-3 mt-2">
          <div className="text-[10px] font-mono-k tracking-widest-k text-red-400 mb-1">— NOT ENABLED YET</div>
          <div className="text-xs font-mono-k text-[#DDDDDD]">{notReadyReason}</div>
        </div>
      ) : conn[id]?.connected ? (
        <div>
          {external_name && <div className="text-xs font-mono-k text-[#DDDDDD] mb-1">Signed in as <span className="text-white">{external_name}</span></div>}
          {connected_at && <div className="text-[10px] font-mono-k text-[#A1A1AA] mb-3">Connected {(connected_at || "").slice(0, 10)}</div>}
          <div className="flex gap-2 mb-3">
            <button onClick={onLoad} className="k-btn text-xs flex-1 justify-center" data-testid={`adconn-${id}-load`}>LIST AD ACCOUNTS</button>
            <button onClick={() => disconnect(id)} className="k-btn text-xs" data-testid={`adconn-${id}-disconnect`}>DISCONNECT</button>
          </div>
          {accounts && (
            <div className="border border-white/10 p-3 bg-[#0A0D14]">
              <div className="text-[10px] font-mono-k tracking-widest-k text-[#FACC15] mb-2">— {accounts.length} AD ACCOUNT{accounts.length === 1 ? "" : "S"}</div>
              {accounts.length === 0 ? (
                <div className="text-xs font-mono-k text-[#A1A1AA]">No ad accounts on this login. Check your access at business.facebook.com or ads.google.com.</div>
              ) : (
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {accounts.slice(0, 20).map((a, i) => (
                    <div key={i} className="text-xs font-mono-k truncate" data-testid={`adconn-${id}-acct-${i}`}>
                      {a.name || a.customer_id || a.account_id} <span className="text-[#A1A1AA]">· {a.account_id || a.customer_id || a.id}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div>
          <p className="text-xs font-mono-k text-[#A1A1AA] mb-3">Connect once — Korporate lists your ad accounts, syncs campaign spend, and can pause/resume placements from your dashboard.</p>
          <button onClick={() => connect(id)} disabled={busy[id]} className="k-btn primary w-full justify-center" data-testid={`adconn-${id}-connect`}>
            {busy[id] ? "REDIRECTING…" : `CONNECT ${name.toUpperCase()}`} <ArrowUpRight size={14} />
          </button>
        </div>
      )}
    </div>
  );

  if (!cfg) return <div className="text-[#A1A1AA] font-mono-k text-sm">Loading…</div>;

  return (
    <div className="mb-8">
      <div className="mb-3 flex items-end justify-between flex-wrap gap-2">
        <div>
          <div className="text-xs font-mono-k tracking-widest-k text-[#FACC15]">— AD ACCOUNTS</div>
          <div className="font-display text-2xl">Route real campaigns through your own accounts.</div>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-0 border-t border-l border-white/10">
        <ProviderCard
          id="meta"
          name="META ADS"
          brand="#1877F2"
          subtitle="Facebook + Instagram · via Meta Marketing API"
          external_name={conn.meta?.external_id}
          connected_at={conn.meta?.connected_at}
          accounts={metaAccounts}
          onLoad={loadMetaAccounts}
          configured={cfg.meta?.configured}
          notReadyReason="Waiting on your Meta App credentials. Set META_APP_ID and META_APP_SECRET in the backend .env, then redeploy. Get them from developers.facebook.com → your Business App → Settings → Basic."
        />
        <ProviderCard
          id="google_ads"
          name="GOOGLE ADS"
          brand="#EA4335"
          subtitle="YouTube + Search · via Google Ads API"
          external_name={conn.google_ads?.external_id}
          connected_at={conn.google_ads?.connected_at}
          accounts={googleAccounts}
          onLoad={loadGoogleAccounts}
          configured={cfg.google_ads?.configured}
          notReadyReason={
            cfg.google_ads?.configured
              ? "Config OK — you can connect."
              : "Waiting on Google Cloud OAuth Client ID/Secret + Developer Token. See console.cloud.google.com → APIs & Services → Credentials, and ads.google.com → Tools → API Center."
          }
        />
      </div>
    </div>
  );
};

const Social = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({});
  const load = () => client.get("/social").then(r => setItems(r.data));
  useEffect(() => {
    load().then(() => { if (window.location.hash === "#music") document.getElementById("music")?.scrollIntoView({ behavior: "smooth" }); });
  }, []);
  const connect = async (platform) => {
    const handle = (form[platform] || "").trim();
    if (!handle) return toast.error("Enter a handle or link");
    try {
      await client.post("/social", { platform, handle });
      load(); toast.success(`${MUSIC_BY_ID[platform]?.label || platform} connected`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const disc = async (p) => { await client.delete(`/social/${p}`); load(); };
  const connected = Object.fromEntries(items.map(s => [s.platform, s]));
  return (
    <div>
      <PageHead eyebrow="SOCIAL INTEGRATIONS" title="Plug in every channel." />
      <AdAccounts />
      <div className="mb-3">
        <div className="text-xs font-mono-k tracking-widest-k text-[#FACC15]">— PROFILE HANDLES</div>
        <div className="font-display text-2xl">Show your feeds on your public bio.</div>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-l border-white/10">
        {PLATFORMS.map(p => {
          const c = connected[p.id];
          return (
            <div key={p.id} className="border-r border-b border-white/10 p-6 bg-[#111318]" data-testid={`social-${p.id}`}>
              <div className="flex items-center gap-3 mb-4">
                <p.icon size={28} weight="duotone" className="text-[#FACC15]" />
                <span className="font-display text-xl">{p.name}</span>
              </div>
              {c ? (
                <div>
                  <div className="text-sm font-mono-k break-all">@{c.handle}</div>
                  {c.profile_url && (
                    <a href={c.profile_url} target="_blank" rel="noreferrer" className="text-xs font-mono-k text-[#FACC15] hover:underline break-all block mb-3" data-testid={`social-link-${p.id}`}>
                      {c.profile_url.replace(/^https?:\/\//, "")}
                    </a>
                  )}
                  <button onClick={() => disc(p.id)} className="k-btn text-xs w-full justify-center" data-testid={`social-disc-${p.id}`}>DISCONNECT</button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input placeholder="username (no @)" onChange={e => setForm({ ...form, [p.id]: e.target.value })} className="k-input" data-testid={`social-input-${p.id}`} />
                  <button onClick={() => connect(p.id)} className="k-btn primary text-xs whitespace-nowrap" data-testid={`social-connect-${p.id}`}>CONNECT</button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="mb-3 mt-10" id="music">
        <div className="text-xs font-mono-k tracking-widest-k text-[#FACC15]">— MUSIC PLATFORMS · ARTISTS & PRODUCERS</div>
        <div className="font-display text-2xl">Let fans stream you in one tap.</div>
        <p className="text-xs font-mono-k text-[#A1A1AA] mt-1">Paste your artist page link. These show as a LISTEN NOW block on your public bio.</p>
      </div>
      <div className="grid md:grid-cols-3 gap-0 border-t border-l border-white/10" data-testid="music-platforms">
        {MUSIC_PLATFORMS.map(p => {
          const c = connected[p.id];
          return (
            <div key={p.id} className="border-r border-b border-white/10 p-6 bg-[#111318]" data-testid={`social-${p.id}`}>
              <div className="flex items-center gap-3 mb-4">
                <p.icon size={28} weight="fill" style={{ color: p.color }} />
                <span className="font-display text-xl">{p.name}</span>
              </div>
              {c ? (
                <div>
                  <a href={c.profile_url} target="_blank" rel="noreferrer" className="text-xs font-mono-k text-[#FACC15] hover:underline truncate block mb-3" data-testid={`social-link-${p.id}`}>{c.profile_url.replace(/^https?:\/\//, "")}</a>
                  <button onClick={() => disc(p.id)} className="k-btn text-xs w-full justify-center" data-testid={`social-disc-${p.id}`}>DISCONNECT</button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input placeholder={p.placeholder} onChange={e => setForm({ ...form, [p.id]: e.target.value })} className="k-input" data-testid={`social-input-${p.id}`} />
                  <button onClick={() => connect(p.id)} className="k-btn primary text-xs whitespace-nowrap" data-testid={`social-connect-${p.id}`}>CONNECT</button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// -------- Wallet --------
const WalletView = ({ user, refresh }) => {
  const [w, setW] = useState({ balance: 0, address: "", transactions: [], breakdown: {}, total_earned: 0 });
  const [dest, setDest] = useState("");
  const [amt, setAmt] = useState("");
  const [method, setMethod] = useState("usdt");
  const [code, setCode] = useState("");
  const [needs2fa, setNeeds2fa] = useState(false);
  const [feePct, setFeePct] = useState(5);
  const load = () => client.get("/wallet").then(r => setW(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    client.get("/plan/limits").then(r => {
      if (typeof r.data?.withdrawal_fee_pct === "number") setFeePct(r.data.withdrawal_fee_pct);
    }).catch(() => {});
  }, []);
  const withdraw = async () => {
    try {
      const { data } = await client.post("/wallet/withdraw", { amount: parseFloat(amt), destination: dest, method, code: code || undefined });
      toast.success(`Payout queued via ${method.toUpperCase()} (ref: ${data.reference?.slice(0, 8) || "…"})`);
      setAmt(""); setDest(""); setCode(""); setNeeds2fa(false); load(); refresh?.();
    } catch (err) {
      if (err?.response?.status === 428) {
        setNeeds2fa(true);
        toast.message("Enter a 2FA code to confirm the withdrawal");
      } else {
        toast.error(err?.response?.data?.detail || "Failed");
      }
    }
  };

  const sources = [
    { key: "sale", label: "PRODUCT SALES", color: "#FACC15" },
    { key: "referral", label: "REFERRAL COMMISSIONS", color: "#FACC15" },
    { key: "subscription_renewal", label: "SUB RENEWALS", color: "#A1A1AA" },
    { key: "trial", label: "TRIALS", color: "#A1A1AA" },
  ];
  const b = w.breakdown || {};
  const destPlaceholder = method === "paypal" ? "your@paypal.email" : method === "payoneer" ? "your@payoneer.email" : "USDT wallet address (TRC20 / ERC20)";
  const amtNum = parseFloat(amt) || 0;
  const estFee = amtNum > 0 ? +(amtNum * feePct / 100).toFixed(2) : 0;
  const estNet = amtNum > 0 ? +(amtNum - estFee).toFixed(2) : 0;

  return (
    <div>
      <PageHead eyebrow="WALLET" title="Your earnings. Your rules." />
      <div className="grid lg:grid-cols-3 gap-0 border-t border-l border-white/10 mb-8">
        <div className="border-r border-b border-white/10 p-8 bg-[#111318]">
          <div className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA] mb-3">AVAILABLE BALANCE</div>
          <div className="font-display text-5xl text-[#FACC15]" data-testid="wallet-balance">${(w.balance || 0).toFixed(2)}</div>
          <div className="text-xs font-mono-k text-[#A1A1AA] mt-3">Total earned: ${(w.total_earned || 0).toFixed(2)}</div>
        </div>
        <div className="border-r border-b border-white/10 p-8 lg:col-span-2">
          <div className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA] mb-3">WITHDRAW · CHOOSE METHOD</div>
          <div className="grid grid-cols-3 gap-2 mb-3">
            <button onClick={() => setMethod("usdt")} className={`k-btn text-xs justify-center ${method === "usdt" ? "primary" : ""}`} data-testid="withdraw-method-usdt">
              <Coins size={14} /> USDT
            </button>
            <button onClick={() => setMethod("paypal")} className={`k-btn text-xs justify-center ${method === "paypal" ? "primary" : ""}`} data-testid="withdraw-method-paypal">
              <Wallet size={14} /> PAYPAL
            </button>
            <button onClick={() => setMethod("payoneer")} className={`k-btn text-xs justify-center ${method === "payoneer" ? "primary" : ""}`} data-testid="withdraw-method-payoneer">
              <Wallet size={14} /> PAYONEER
            </button>
          </div>
          <div className="grid md:grid-cols-[1fr_1fr_auto] gap-3">
            <input placeholder="Amount USD" type="number" step="0.01" min="5" value={amt} onChange={e => setAmt(e.target.value)} className="k-input" data-testid="wallet-amt" />
            <input placeholder={destPlaceholder} value={dest} onChange={e => setDest(e.target.value)} className="k-input" data-testid="wallet-dest" />
            <button onClick={withdraw} className="k-btn primary" data-testid="wallet-withdraw"><CurrencyDollar size={16} /> WITHDRAW</button>
          </div>
          {needs2fa && (
            <div className="mt-3 border border-[#FACC15] bg-[#FACC15]/10 p-3" data-testid="wallet-2fa-block">
              <div className="flex items-center gap-2 mb-2 text-xs font-mono-k tracking-widest-k text-[#FACC15]"><ShieldCheck size={14} /> 2FA CONFIRMATION</div>
              <input inputMode="numeric" maxLength={6} placeholder="6-digit code" value={code} onChange={e => setCode(e.target.value)} className="k-input font-mono-k tracking-widest text-center" data-testid="wallet-2fa-code" autoFocus />
            </div>
          )}
          {amtNum >= 5 && (
            <div className="mt-3 border border-white/10 bg-[#0A0D14] p-3 text-xs font-mono-k" data-testid="withdraw-fee-breakdown">
              <div className="flex items-center justify-between text-[#A1A1AA]"><span>Requested</span><span>${amtNum.toFixed(2)}</span></div>
              <div className="flex items-center justify-between text-[#A1A1AA]"><span>Platform fee ({feePct}%)</span><span>−${estFee.toFixed(2)}</span></div>
              <div className="flex items-center justify-between border-t border-white/10 mt-2 pt-2 text-[#FACC15]"><span>You'll receive</span><span className="font-display text-lg">${estNet.toFixed(2)}</span></div>
            </div>
          )}
          <div className="text-[10px] font-mono-k tracking-widest-k text-[#A1A1AA] mt-3">
            {user?.totp_enabled ? "2FA required · " : ""}Min $5.00 · {feePct}% platform fee · Payouts processed within 48h · {method === "paypal" ? "Sent to your PayPal email" : method === "payoneer" ? "Sent to the email on your Payoneer account" : "Sent to your USDT wallet (TRC20 / ERC20 / BSC)"}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-t border-l border-white/10 mb-8">
        {sources.map(s => (
          <div key={s.key} className="border-r border-b border-white/10 p-5 bg-[#111318]">
            <div className="text-[10px] font-mono-k tracking-widest-k text-[#A1A1AA] mb-2">{s.label}</div>
            <div className="font-display text-2xl" style={{ color: s.color }}>${(b[s.key] || 0).toFixed(2)}</div>
          </div>
        ))}
      </div>

      <div className="border border-white/10">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/10 text-xs font-mono-k tracking-widest-k text-[#A1A1AA]">
              <th className="text-left p-3">TYPE</th><th className="text-left p-3">LABEL</th><th className="text-left p-3">STATUS</th><th className="text-right p-3">AMOUNT</th><th className="text-left p-3">DATE</th>
            </tr>
          </thead>
          <tbody className="font-mono-k text-sm">
            {w.transactions.length === 0 && <tr><td colSpan="5" className="p-6 text-[#A1A1AA]">No transactions yet.</td></tr>}
            {w.transactions.map(t => (
              <tr key={t.id} className="border-b border-white/10">
                <td className="p-3 tracking-widest-k text-xs">{t.type?.toUpperCase()}</td>
                <td className="p-3">{t.label}</td>
                <td className="p-3 text-xs tracking-widest-k">
                  {t.type === "withdrawal" ? (
                    <span className={t.status === "paid" ? "text-[#FACC15]" : t.status === "rejected" ? "text-red-400" : "text-[#A1A1AA]"}>
                      {(t.status || "pending").toUpperCase()}
                    </span>
                  ) : (
                    <span className="text-[#FACC15]">CONFIRMED</span>
                  )}
                </td>
                <td className={`p-3 text-right ${t.amount >= 0 ? "text-[#FACC15]" : "text-red-400"}`}>${(t.amount || 0).toFixed(2)}</td>
                <td className="p-3 text-[#A1A1AA]">{(t.created_at || "").slice(0, 10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// -------- Referrals --------
const Referrals = () => {
  const [d, setD] = useState(null);
  const [claims, setClaims] = useState([]);
  const [showClaim, setShowClaim] = useState(false);
  const [claimForm, setClaimForm] = useState({ referred_email: "", reason: "" });
  const [submitting, setSubmitting] = useState(false);
  const prevCount = useRef(null);
  const prevClaimStatus = useRef({});

  const load = async () => {
    try {
      const { data } = await client.get("/referral");
      // Real-time toast when count grows while page is open
      if (prevCount.current !== null && data.total_referrals > prevCount.current) {
        const diff = data.total_referrals - prevCount.current;
        toast.success(`${diff} new referral${diff > 1 ? "s" : ""} just landed`);
      }
      prevCount.current = data.total_referrals;
      setD(data);
    } catch (err) { /* ignore transient */ }
  };
  const loadClaims = async () => {
    try {
      const { data } = await client.get("/referral/claims");
      // Toast when an existing pending claim resolves (approved/rejected)
      data.forEach(c => {
        const prev = prevClaimStatus.current[c.id];
        if (prev && prev === "pending" && c.status !== "pending") {
          if (c.status === "approved") {
            toast.success(`Referral claim approved — ${c.referred_email} is now in your list`);
            load();  // refresh referral counts + list immediately
          } else if (c.status === "rejected") {
            toast.message(`Referral claim rejected — ${c.referred_email}${c.admin_note ? ` (${c.admin_note})` : ""}`);
          }
        }
        prevClaimStatus.current[c.id] = c.status;
      });
      setClaims(data);
    } catch (err) { /* ignore */ }
  };

  useEffect(() => { load(); loadClaims(); }, []);
  // Poll every 15s so new signups + claim approvals appear without a manual reload
  useEffect(() => {
    const id = setInterval(() => { load(); loadClaims(); }, 15000);
    return () => clearInterval(id);
  }, []);

  const copy = () => { if (d?.share_url) { navigator.clipboard.writeText(d.share_url); toast.success("Referral link copied"); } };

  const submitClaim = async (e) => {
    e.preventDefault();
    if (!claimForm.referred_email.trim()) return toast.error("Enter their email");
    setSubmitting(true);
    try {
      await client.post("/referral/claim", claimForm);
      toast.success("Claim submitted — admin will review shortly");
      setClaimForm({ referred_email: "", reason: "" });
      setShowClaim(false);
      loadClaims();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    setSubmitting(false);
  };

  const statusColor = (s) => s === "approved" ? "#22C55E" : s === "rejected" ? "#EF4444" : "#FACC15";

  if (!d) return <div className="font-mono-k text-[#A1A1AA]">Loading…</div>;
  return (
    <div>
      <PageHead
        eyebrow="REFERRALS"
        title={`Earn ${d.commission_pct}% for life.`}
        action={
          <div className="flex gap-2">
            <button onClick={() => { load(); loadClaims(); toast.success("Refreshed"); }} className="k-btn text-xs" data-testid="btn-refresh-ref"><ArrowRight size={14} /> REFRESH</button>
            <button onClick={copy} className="k-btn primary" data-testid="btn-copy-ref"><Copy size={16} /> COPY LINK</button>
          </div>
        }
      />
      <div className="border border-[#FACC15]/40 bg-[#111318] p-6 mb-8">
        <div className="text-xs font-mono-k tracking-widest-k text-[#FACC15] mb-2">— YOUR REFERRAL LINK</div>
        <div className="font-mono-k text-sm md:text-base text-white break-all mb-3" data-testid="ref-link">{d.share_url}</div>
        <div className="text-xs font-mono-k text-[#A1A1AA]">Share it. When someone signs up and upgrades to Pro (or trial), you earn ${((d.commission_pct/100) * 19).toFixed(2)} on subscription and ${((d.commission_pct/100) * 7).toFixed(2)} on trial — paid instantly to your Korporate wallet.</div>
        <div className="text-[10px] font-mono-k text-[#A1A1AA] mt-3">Auto-refreshes every 15 seconds · new signups toast the moment they land.</div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-t border-l border-white/10 mb-8">
        <div className="border-r border-b border-white/10 p-6 bg-[#111318]"><div className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA] mb-2">TOTAL REFERRALS</div><div className="font-display text-3xl" data-testid="stat-total-referrals">{d.total_referrals}</div></div>
        <div className="border-r border-b border-white/10 p-6 bg-[#111318]"><div className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA] mb-2">PRO REFERRALS</div><div className="font-display text-3xl text-[#FACC15]">{d.pro_referrals}</div></div>
        <div className="border-r border-b border-white/10 p-6 bg-[#111318]"><div className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA] mb-2">TOTAL EARNED</div><div className="font-display text-3xl text-[#FACC15]">${d.total_earned.toFixed(2)}</div></div>
        <div className="border-r border-b border-white/10 p-6 bg-[#111318]"><div className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA] mb-2">COMMISSION</div><div className="font-display text-3xl">{d.commission_pct}%</div></div>
      </div>

      {/* Missing-referral claim panel */}
      <div className="border border-[#00E5FF]/30 bg-[#00E5FF]/5 p-5 mb-8" data-testid="claim-panel">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-[240px]">
            <div className="text-xs font-mono-k tracking-widest-k text-[#00E5FF] mb-1">— MISSING A REFERRAL?</div>
            <div className="font-display text-xl mb-1">Claim someone who signed up before we fixed tracking.</div>
            <div className="text-xs font-mono-k text-[#A1A1AA]">Enter their email — the admin team will review and attribute it to your link.</div>
          </div>
          <button onClick={() => setShowClaim(true)} className="k-btn primary" data-testid="btn-open-claim"><Plus size={14} /> CLAIM MISSING</button>
        </div>
        {claims.length > 0 && (
          <div className="mt-4 border-t border-white/10 pt-3">
            <div className="text-[10px] font-mono-k tracking-widest-k text-[#A1A1AA] mb-2">YOUR CLAIMS · {claims.length}</div>
            <div className="space-y-1">
              {claims.slice(0, 10).map(c => (
                <div key={c.id} className="flex items-center justify-between text-xs font-mono-k gap-3" data-testid={`claim-row-${c.id}`}>
                  <div className="truncate flex-1">{c.referred_email}</div>
                  <div className="text-[#A1A1AA] whitespace-nowrap">{(c.created_at || "").slice(0, 10)}</div>
                  <div className="text-[10px] px-2 py-0.5 border tracking-widest-k" style={{ color: statusColor(c.status), borderColor: statusColor(c.status) }}>{(c.status || "pending").toUpperCase()}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Claim modal */}
      {showClaim && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setShowClaim(false)} data-testid="claim-modal">
          <form onClick={e => e.stopPropagation()} onSubmit={submitClaim} className="w-full max-w-md border border-white/10 bg-[#111318] p-6 space-y-3">
            <div className="text-xs font-mono-k tracking-widest-k text-[#00E5FF]">— CLAIM MISSING REFERRAL</div>
            <div className="font-display text-2xl">Who should have been yours?</div>
            <input required type="email" placeholder="their@email.com" value={claimForm.referred_email} onChange={e => setClaimForm({ ...claimForm, referred_email: e.target.value })} className="k-input w-full" data-testid="claim-email" />
            <textarea rows={3} placeholder="Optional: how do you know they used your link? (Screenshot links, dates, campaign, etc.)" value={claimForm.reason} onChange={e => setClaimForm({ ...claimForm, reason: e.target.value })} className="k-input w-full text-sm" data-testid="claim-reason" />
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setShowClaim(false)} className="k-btn text-xs" data-testid="claim-cancel">CANCEL</button>
              <button type="submit" disabled={submitting} className="k-btn primary text-xs" data-testid="claim-submit">{submitting ? "SUBMITTING…" : "SUBMIT CLAIM"}</button>
            </div>
          </form>
        </div>
      )}

      <div className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA] mb-3">— YOUR REFERRALS</div>
      <div className="border border-white/10 mb-8">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/10 text-xs font-mono-k tracking-widest-k text-[#A1A1AA]">
              <th className="text-left p-3">NAME</th><th className="text-left p-3">EMAIL</th><th className="text-left p-3">PLAN</th><th className="text-left p-3">JOINED</th>
            </tr>
          </thead>
          <tbody className="font-mono-k text-sm">
            {d.referrals.length === 0 && <tr><td colSpan="4" className="p-6 text-[#A1A1AA]">No referrals yet. Copy your link and share it.</td></tr>}
            {d.referrals.map(r => (
              <tr key={r.id} className="border-b border-white/10" data-testid={`ref-user-${r.id}`}>
                <td className="p-3">{r.name}</td>
                <td className="p-3 text-[#A1A1AA]">{r.email}</td>
                <td className="p-3"><span className={`px-2 py-1 text-[10px] tracking-widest-k ${r.plan === "pro" ? "bg-[#FACC15] text-black" : "border border-white/20 text-[#A1A1AA]"}`}>{r.plan?.toUpperCase()}</span></td>
                <td className="p-3 text-[#A1A1AA]">{(r.joined_at || "").slice(0, 10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA] mb-3">— EARNINGS</div>
      <div className="border border-white/10">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/10 text-xs font-mono-k tracking-widest-k text-[#A1A1AA]">
              <th className="text-left p-3">SOURCE</th><th className="text-right p-3">AMOUNT</th><th className="text-left p-3">DATE</th>
            </tr>
          </thead>
          <tbody className="font-mono-k text-sm">
            {(d.recent_earnings || []).length === 0 && <tr><td colSpan="3" className="p-6 text-[#A1A1AA]">No earnings yet.</td></tr>}
            {(d.recent_earnings || []).map(t => (
              <tr key={t.id} className="border-b border-white/10">
                <td className="p-3">{t.label}</td>
                <td className="p-3 text-right text-[#FACC15]">${(t.amount || 0).toFixed(2)}</td>
                <td className="p-3 text-[#A1A1AA]">{(t.created_at || "").slice(0, 10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// -------- Analytics --------
const Analytics = () => {
  const [d, setD] = useState(null);
  useEffect(() => { client.get("/analytics").then(r => setD(r.data)); }, []);
  return (
    <div>
      <PageHead eyebrow="ANALYTICS" title="The whole picture." />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 border-t border-l border-white/10 mb-8">
        <div className="border-r border-b border-white/10"><Stat label="IMPRESSIONS" value={d?.impressions ?? 0} /></div>
        <div className="border-r border-b border-white/10"><Stat label="CLICKS" value={d?.total_clicks ?? 0} /></div>
        <div className="border-r border-b border-white/10"><Stat label="LEADS" value={d?.total_leads ?? 0} /></div>
        <div className="border-r border-b border-white/10"><Stat label="REVENUE" value={`$${d?.revenue ?? 0}`} /></div>
      </div>
      <div className="border border-white/10 p-6 mb-8">
        <div className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA] mb-4">CLICKS · LAST 7 DAYS</div>
        <div className="flex items-end gap-2 h-56">
          {(d?.series || []).map((day, i) => {
            const max = Math.max(1, ...(d?.series || []).map(x => x.clicks));
            const h = Math.max(4, (day.clicks / max) * 200);
            return (
              <div key={i} className="flex-1 flex flex-col items-center gap-2">
                <div className="w-full bg-[#FACC15]" style={{ height: h }} />
                <div className="text-[10px] font-mono-k text-[#A1A1AA]">{day.date.slice(5)}</div>
                <div className="text-[10px] font-mono-k">{day.clicks}</div>
              </div>
            );
          })}
        </div>
      </div>

      {d?.payment_split && (
        <div className="border border-white/10 p-6" data-testid="payment-split">
          <div className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA] mb-4">SALES BY PAYMENT RAIL</div>
          {(() => {
            const ps = d.payment_split;
            const total = (ps.paypal?.revenue || 0) + (ps.nowpayments?.revenue || 0) + (ps.other?.revenue || 0);
            const rails = [
              { key: "paypal", label: "PAYPAL / CARD", color: "#FACC15" },
              { key: "nowpayments", label: "USDT / CRYPTO", color: "#00E5FF" },
              { key: "other", label: "OTHER / MANUAL", color: "#A1A1AA" },
            ];
            if (total === 0) {
              return <div className="text-sm font-mono-k text-[#A1A1AA]">No sales yet — once orders start flowing, you'll see PayPal vs Crypto revenue split here.</div>;
            }
            return (
              <>
                <div className="flex h-4 mb-4 overflow-hidden border border-white/10">
                  {rails.map(r => {
                    const rev = ps[r.key]?.revenue || 0;
                    const pct = total > 0 ? (rev / total) * 100 : 0;
                    return pct > 0 ? <div key={r.key} style={{ width: `${pct}%`, background: r.color }} title={`${r.label}: $${rev.toFixed(2)}`} data-testid={`split-bar-${r.key}`} /> : null;
                  })}
                </div>
                <div className="grid md:grid-cols-3 gap-3">
                  {rails.map(r => {
                    const rev = ps[r.key]?.revenue || 0;
                    const cnt = ps[r.key]?.count || 0;
                    const pct = total > 0 ? (rev / total) * 100 : 0;
                    return (
                      <div key={r.key} className="border border-white/10 p-3" data-testid={`split-card-${r.key}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="inline-block w-3 h-3" style={{ background: r.color }} />
                          <span className="text-[10px] font-mono-k tracking-widest-k text-[#A1A1AA]">{r.label}</span>
                        </div>
                        <div className="font-display text-2xl" style={{ color: r.color }}>${rev.toFixed(2)}</div>
                        <div className="text-[10px] font-mono-k text-[#A1A1AA] mt-1">{cnt} order{cnt === 1 ? "" : "s"} · {pct.toFixed(0)}%</div>
                      </div>
                    );
                  })}
                </div>
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
};

// -------- Pricing / Upgrade --------
const PLAN_FEATURES = {
  free: {
    tag: "FREE",
    price: "$0",
    subtitle: "Get started, get paid.",
    color: "#A1A1AA",
    features: [
      "Unlimited bio links",
      "Public bio page with theme picker",
      "Up to 3 shop products",
      "1 lead / sales funnel",
      "Basic campaign manager",
      "7-day analytics",
      "20% referral commission",
      "USDT + PayPal payouts (72h, $5 min)",
      "10 AI Copilot generations / month",
    ],
  },
  pro: {
    tag: "PRO",
    price: "$19",
    subtitle: "Convert like a pro.",
    color: "#FACC15",
    features: [
      "Everything in Free",
      "Unlimited shop products",
      "10 sales / lead funnels",
      "Priority campaign routing",
      "90-day analytics",
      "25% referral commission",
      "48h payouts",
      "100 AI Copilot gens / month",
      "Custom domain support",
      "Email support",
    ],
    highlight: "MOST POPULAR",
  },
  boss: {
    tag: "BOSS UP",
    price: "$50",
    subtitle: "Take over your market.",
    color: "#00E5FF",
    features: [
      "Everything in Pro",
      "Unlimited funnels",
      "Auto-optimize campaigns (AI)",
      "12-month analytics",
      "30% referral commission",
      "24h priority payouts",
      "Unlimited AI Copilot",
      "Custom domain + email",
      "VIP chat support",
      "Early access to new features",
    ],
    highlight: "BEST VALUE",
  },
};

const Pricing = ({ user, refresh }) => {
  const nav = useNavigate();
  const [sub, setSub] = useState(null);
  const [limits, setLimits] = useState(null);
  useEffect(() => { client.get("/payments/paypal/subscription").then(r => setSub(r.data)).catch(() => {}); }, [user?.plan]);
  useEffect(() => { client.get("/plan/limits").then(r => setLimits(r.data)).catch(() => {}); }, [user?.plan]);

  const buy = async (provider, kind = "pro") => {
    try {
      const { data } = await client.post("/payments/checkout", { kind, provider });
      nav(data.approve_url);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const cancelSub = async () => {
    if (!window.confirm("Cancel your subscription? You'll lose plan features immediately.")) return;
    try {
      await client.post("/payments/paypal/subscription/cancel", { reason: "user requested" });
      toast.success("Subscription cancelled");
      refresh?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const currentPlan = user?.plan || "free";
  const trialUsed = user?.has_used_trial;
  const activeSub = sub?.active ? sub.subscription : null;
  const currentTierName = currentPlan === "boss" ? "Boss Up" : currentPlan === "pro" ? "Pro" : "Free";

  const renderTier = (key) => {
    const t = PLAN_FEATURES[key];
    const isCurrent = currentPlan === key;
    const isFree = key === "free";
    const isBoss = key === "boss";
    return (
      <div key={key} className={`border ${isBoss ? "border-[#00E5FF]" : key === "pro" ? "border-[#FACC15]" : "border-white/10"} bg-[#111318] p-8 relative flex flex-col`} data-testid={`plan-card-${key}`}>
        {t.highlight && (
          <div className="absolute -top-3 left-6 px-3 py-1 border font-mono-k text-[10px] tracking-widest-k" style={{ background: t.color, color: "#050505", borderColor: t.color }}>{t.highlight}</div>
        )}
        <div className="flex items-baseline justify-between mb-2">
          <h3 className="font-display text-4xl tracking-tighter" style={{ color: t.color }}>{t.tag}</h3>
          <div className="font-mono-k text-lg" style={{ color: t.color }}>{t.price}<span className="text-xs text-[#A1A1AA]">/mo</span></div>
        </div>
        <div className="text-xs font-mono-k text-[#A1A1AA] mb-6">{t.subtitle}</div>
        <ul className="space-y-2 mb-6 text-sm font-mono-k text-[#DDDDDD] flex-1">
          {t.features.map((f, i) => (
            <li key={i} className="flex items-start gap-2">
              <Check size={14} className="mt-1 flex-shrink-0" style={{ color: t.color }} />
              <span>{f}</span>
            </li>
          ))}
        </ul>
        {isCurrent ? (
          <div className="k-btn w-full justify-center" style={{ borderColor: t.color, color: t.color }} data-testid={`plan-active-${key}`}>YOUR CURRENT PLAN</div>
        ) : isFree ? (
          <div className="text-xs font-mono-k text-[#A1A1AA] text-center py-3">Included by default</div>
        ) : (
          <div className="grid gap-2">
            <button onClick={() => buy("paypal", key)} className="k-btn primary w-full justify-center" style={{ background: t.color, borderColor: t.color, color: "#050505" }} data-testid={`buy-${key}-paypal`}>PAYPAL / CARD <ArrowRight size={16} /></button>
            <button onClick={() => buy("nowpayments", key)} className="k-btn w-full justify-center" style={{ borderColor: t.color, color: t.color }} data-testid={`buy-${key}-crypto`}>USDT / CRYPTO <ArrowRight size={16} /></button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <PageHead eyebrow="PLANS" title={`You're on ${currentTierName}. Pick your next move.`} />
      <PromoRedeem user={user} refresh={refresh} />

      {activeSub && (
        <div className="border border-[#FACC15]/40 bg-[#111318] p-6 mb-6 grid md:grid-cols-4 gap-4 items-center" data-testid="active-subscription">
          <div>
            <div className="text-xs font-mono-k tracking-widest-k text-[#FACC15] mb-1">— ACTIVE SUBSCRIPTION</div>
            <div className="font-display text-2xl">{currentTierName} Plan</div>
          </div>
          <div><div className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA]">STATUS</div><div className="font-mono-k text-sm text-[#FACC15]">{activeSub.status}</div></div>
          <div><div className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA]">NEXT BILLING</div><div className="font-mono-k text-sm">{activeSub.next_billing_time ? new Date(activeSub.next_billing_time).toLocaleDateString() : "—"}</div></div>
          <button onClick={cancelSub} className="k-btn text-xs" data-testid="cancel-subscription">CANCEL</button>
        </div>
      )}

      {currentPlan === "free" && !trialUsed && (
        <div className="border border-[#FACC15] bg-[#FACC15]/10 p-6 mb-6 flex items-center gap-6 flex-wrap" data-testid="trial-offer">
          <div className="flex-1 min-w-[240px]">
            <div className="text-xs font-mono-k tracking-widest-k text-[#FACC15] mb-1">— ONE-TIME OFFER</div>
            <div className="font-display text-2xl md:text-3xl tracking-tighter">Try Pro for 30 days · $7</div>
            <div className="text-xs font-mono-k text-[#A1A1AA] mt-1">Full Pro access for 30 days. One-time charge — no auto-renew.</div>
          </div>
          <div className="grid gap-2">
            <button onClick={() => buy("paypal", "trial")} className="k-btn primary" data-testid="buy-trial-paypal">TRIAL VIA PAYPAL <ArrowRight size={16} /></button>
            <button onClick={() => buy("nowpayments", "trial")} className="k-btn" data-testid="buy-trial-crypto">TRIAL VIA USDT <ArrowRight size={16} /></button>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-4">
        {renderTier("free")}
        {renderTier("pro")}
        {renderTier("boss")}
      </div>

      {limits && (
        <div className="mt-8 border border-white/10 p-6 bg-[#111318]" data-testid="usage-panel">
          <div className="text-xs font-mono-k tracking-widest-k text-[#FACC15] mb-3">— YOUR USAGE</div>
          <div className="grid md:grid-cols-4 gap-4 text-sm">
            <div><div className="text-[10px] font-mono-k text-[#A1A1AA] tracking-widest-k">PRODUCTS</div><div className="font-display text-2xl">{limits.used.products} / {limits.limits.products === 999 ? "∞" : limits.limits.products}</div></div>
            <div><div className="text-[10px] font-mono-k text-[#A1A1AA] tracking-widest-k">FUNNELS</div><div className="font-display text-2xl">{limits.used.funnels} / {limits.limits.funnels === 999 ? "∞" : limits.limits.funnels}</div></div>
            <div><div className="text-[10px] font-mono-k text-[#A1A1AA] tracking-widest-k">REFERRAL RATE</div><div className="font-display text-2xl">{limits.referral_pct}%</div></div>
            <div><div className="text-[10px] font-mono-k text-[#A1A1AA] tracking-widest-k">PAYOUT SPEED</div><div className="font-display text-2xl">{limits.payout_hours}h</div></div>
          </div>
        </div>
      )}
    </div>
  );
};

// -------- Push Notifications Card --------
const PushCard = () => {
  const supported = isPushSupported();
  const [permission, setPermission] = useState(supported ? getPushPermission() : "unsupported");
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!supported) return;
    currentSubscription().then(s => setSubscribed(!!s));
  }, [supported]);
  const enable = async () => {
    setBusy(true);
    try { await subscribeToPush(); setSubscribed(true); setPermission("granted"); toast.success("Push notifications enabled"); }
    catch (err) { toast.error(err.message || "Failed"); }
    finally { setBusy(false); }
  };
  const disable = async () => {
    setBusy(true);
    try { await unsubscribeFromPush(); setSubscribed(false); toast.success("Push notifications disabled"); }
    catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };
  const test = async () => {
    try { await testPush(); toast.message("Test push sent — check your device"); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="border border-white/10 bg-[#111318] p-6">
      <div className="text-xs font-mono-k tracking-widest-k text-[#FACC15] mb-4 flex items-center gap-2">
        <PaperPlaneTilt size={14} /> — PUSH NOTIFICATIONS
      </div>
      {!supported && (
        <div className="text-sm font-mono-k text-[#A1A1AA]">Push notifications aren't supported on this browser. Try Chrome, Edge, Firefox or the installed PWA.</div>
      )}
      {supported && (
        <div>
          <p className="text-xs font-mono-k text-[#A1A1AA] mb-4">Get real-time alerts on new leads, sales and referral commissions — even when the tab is closed. Works on your phone once you install Korporate to your home screen.</p>
          <div className="flex items-center gap-2 mb-4 text-sm font-mono-k">
            {subscribed ? (
              <><span className="w-2 h-2 bg-[#FACC15]" /> ENABLED on this device</>
            ) : permission === "denied" ? (
              <><span className="w-2 h-2 bg-red-400" /> BLOCKED — enable notifications in browser settings</>
            ) : (
              <><span className="w-2 h-2 bg-red-400" /> DISABLED</>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            {subscribed ? (
              <>
                <button onClick={test} disabled={busy} className="k-btn text-xs" data-testid="btn-push-test">SEND TEST</button>
                <button onClick={disable} disabled={busy} className="k-btn text-xs" data-testid="btn-push-off">DISABLE</button>
              </>
            ) : (
              <button onClick={enable} disabled={busy || permission === "denied"} className="k-btn primary text-xs" data-testid="btn-push-on">
                <PaperPlaneTilt size={14} /> ENABLE PUSH
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// -------- 2FA Card --------
const TwoFactorCard = ({ user, refresh }) => {
  const [status, setStatus] = useState({ enabled: user?.totp_enabled || false });
  const [setup, setSetup] = useState(null); // {secret, otpauth_uri}
  const [code, setCode] = useState("");
  const [disableForm, setDisableForm] = useState({ password: "", code: "" });
  const [showDisable, setShowDisable] = useState(false);
  const [recovery, setRecovery] = useState(null); // {codes: [...]} shown after enable / regenerate
  const [recoveryStatus, setRecoveryStatus] = useState({ remaining: 0 });
  const [showRegen, setShowRegen] = useState(false);
  const [regenCode, setRegenCode] = useState("");
  const load = () => {
    client.get("/auth/2fa/status").then(r => setStatus(r.data));
    client.get("/auth/2fa/recovery-codes/status").then(r => setRecoveryStatus(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const startSetup = async () => {
    try { const { data } = await client.post("/auth/2fa/setup"); setSetup(data); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const confirm = async (e) => {
    e.preventDefault();
    try {
      const { data } = await client.post("/auth/2fa/enable", { code });
      toast.success("Two-factor auth enabled");
      setSetup(null); setCode("");
      setRecovery({ codes: data.recovery_codes });
      load(); refresh?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Invalid code"); }
  };

  const disable = async (e) => {
    e.preventDefault();
    try {
      await client.post("/auth/2fa/disable", disableForm);
      toast.success("2FA disabled");
      setDisableForm({ password: "", code: "" }); setShowDisable(false);
      load(); refresh?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const regenerate = async (e) => {
    e.preventDefault();
    try {
      const { data } = await client.post("/auth/2fa/recovery-codes/regenerate", { code: regenCode });
      setRecovery({ codes: data.recovery_codes });
      setShowRegen(false); setRegenCode("");
      toast.success("New recovery codes generated");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const downloadCodes = () => {
    if (!recovery?.codes) return;
    const blob = new Blob([
      `Korporate — 2FA Recovery Codes\nGenerated: ${new Date().toISOString()}\n\nEach code works exactly once. Store these somewhere safe.\n\n${recovery.codes.join("\n")}\n`
    ], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "korporate-recovery-codes.txt"; a.click();
    URL.revokeObjectURL(url);
  };
  const copyCodes = () => { navigator.clipboard.writeText((recovery?.codes || []).join("\n")); toast.success("Copied"); };

  return (
    <div id="2fa" className="border border-white/10 bg-[#111318] p-6 scroll-mt-24">
      <div className="text-xs font-mono-k tracking-widest-k text-[#FACC15] mb-4 flex items-center gap-2">
        <ShieldCheck size={14} /> — TWO-FACTOR AUTH
      </div>

      {recovery && (
        <div className="border border-[#FACC15] bg-[#FACC15]/10 p-4 mb-4" data-testid="recovery-codes-block">
          <div className="text-xs font-mono-k tracking-widest-k text-[#FACC15] mb-2">— SAVE THESE 8 RECOVERY CODES</div>
          <p className="text-xs font-mono-k text-[#A1A1AA] mb-3">Each works exactly once. Store them somewhere safe (password manager, printed sheet). You won't see them again.</p>
          <div className="grid grid-cols-2 gap-2 font-mono-k text-sm text-white mb-3 bg-[#0A0D14] p-3 border border-white/10">
            {recovery.codes.map((c, i) => <div key={i} data-testid={`rc-${i}`}>{c}</div>)}
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={copyCodes} className="k-btn text-xs" data-testid="rc-copy"><Copy size={12} /> COPY</button>
            <button onClick={downloadCodes} className="k-btn text-xs" data-testid="rc-download">DOWNLOAD .TXT</button>
            <button onClick={() => setRecovery(null)} className="k-btn primary text-xs" data-testid="rc-dismiss">I'VE SAVED THEM</button>
          </div>
        </div>
      )}

      {status.enabled && !showDisable && !recovery && (
        <div>
          <div className="flex items-center gap-2 mb-3 text-sm font-mono-k">
            <span className="w-2 h-2 bg-[#FACC15]" /> ENABLED — Every login (and withdrawal) requires a 6-digit code
          </div>
          <div className="text-xs font-mono-k text-[#A1A1AA] mb-4">Recovery codes remaining: <span className="text-[#FACC15]">{recoveryStatus.remaining}</span> of 8</div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => setShowRegen(true)} className="k-btn text-xs" data-testid="btn-regen-recovery">REGENERATE CODES</button>
            <button onClick={() => setShowDisable(true)} className="k-btn text-xs" data-testid="btn-tfa-disable">DISABLE 2FA</button>
          </div>
          {showRegen && (
            <form onSubmit={regenerate} className="mt-4 grid gap-2">
              <p className="text-xs font-mono-k text-[#A1A1AA]">Enter a current 6-digit code to invalidate your old recovery codes and generate 8 fresh ones.</p>
              <input inputMode="numeric" maxLength={6} placeholder="6-digit code" value={regenCode} onChange={e => setRegenCode(e.target.value)} className="k-input font-mono-k tracking-widest text-center" required data-testid="regen-code" />
              <div className="flex gap-2">
                <button type="button" onClick={() => { setShowRegen(false); setRegenCode(""); }} className="k-btn text-xs flex-1 justify-center">CANCEL</button>
                <button className="k-btn primary text-xs flex-1 justify-center" data-testid="regen-submit">GENERATE NEW CODES</button>
              </div>
            </form>
          )}
        </div>
      )}

      {status.enabled && showDisable && (
        <form onSubmit={disable} className="grid gap-3">
          <p className="text-xs font-mono-k text-[#A1A1AA]">Confirm your password and a current 2FA code to turn off two-factor auth.</p>
          <input type="password" placeholder="Password" value={disableForm.password} onChange={e => setDisableForm({ ...disableForm, password: e.target.value })} className="k-input" required data-testid="tfa-disable-pw" />
          <input inputMode="numeric" maxLength={6} placeholder="6-digit code" value={disableForm.code} onChange={e => setDisableForm({ ...disableForm, code: e.target.value })} className="k-input font-mono-k tracking-widest text-center" required data-testid="tfa-disable-code" />
          <div className="flex gap-2">
            <button type="button" onClick={() => setShowDisable(false)} className="k-btn text-xs flex-1 justify-center">CANCEL</button>
            <button className="k-btn primary text-xs flex-1 justify-center" data-testid="tfa-disable-submit">CONFIRM DISABLE</button>
          </div>
        </form>
      )}

      {!status.enabled && !setup && (
        <div>
          <div className="flex items-center gap-2 mb-3 text-sm font-mono-k">
            <span className="w-2 h-2 bg-red-400" /> DISABLED
          </div>
          <p className="text-xs font-mono-k text-[#A1A1AA] mb-4">Protect your account and wallet with a 6-digit code from an authenticator app (Google Authenticator, 1Password, Authy…). You'll also get 8 backup recovery codes.</p>
          <button onClick={startSetup} className="k-btn primary text-xs" data-testid="btn-tfa-setup"><ShieldCheck size={14} /> ENABLE 2FA</button>
        </div>
      )}

      {setup && (
        <form onSubmit={confirm} className="grid gap-4">
          <p className="text-xs font-mono-k text-[#A1A1AA]">1. Scan this QR with your authenticator app.</p>
          <div className="bg-white p-4 inline-block mx-auto" data-testid="tfa-qr">
            <QRCodeCanvas value={setup.otpauth_uri} size={180} bgColor="#ffffff" fgColor="#050505" />
          </div>
          <div>
            <div className="text-[10px] font-mono-k tracking-widest-k text-[#A1A1AA] mb-1">OR ENTER MANUALLY</div>
            <div className="bg-[#0A0D14] border border-white/10 p-3 font-mono-k text-sm text-[#FACC15] break-all" data-testid="tfa-secret">{setup.secret}</div>
          </div>
          <div>
            <label className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA]">2. ENTER THE CURRENT 6-DIGIT CODE</label>
            <input inputMode="numeric" maxLength={6} value={code} onChange={e => setCode(e.target.value)} className="k-input mt-1 font-mono-k tracking-widest text-center text-lg" required data-testid="tfa-confirm-code" placeholder="123456" />
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => { setSetup(null); setCode(""); }} className="k-btn text-xs flex-1 justify-center">CANCEL</button>
            <button className="k-btn primary text-xs flex-1 justify-center" data-testid="tfa-confirm-submit">CONFIRM & ENABLE</button>
          </div>
        </form>
      )}
    </div>
  );
};

// -------- Settings --------
const Settings = ({ user, refresh }) => {
  // Hash-anchor scroll (e.g. coming from the 2FA nudge → scroll to #2fa)
  useEffect(() => {
    const hash = window.location.hash.replace("#", "");
    if (!hash) return;
    const scroll = () => {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    // Wait for the Settings subtree to mount
    const t = setTimeout(scroll, 300);
    return () => clearTimeout(t);
  }, []);

  const [f, setF] = useState({
    name: user?.name || "",
    bio: user?.bio || "",
    about: user?.about || "",
    title: user?.title || "",
    avatar: user?.avatar || "",
    store_logo: user?.store_logo || "",
    slug: user?.slug || "",
    wallet_address: user?.wallet_address || "",
    show_shop: user?.show_shop !== false,
    show_links: user?.show_links !== false,
    show_social: user?.show_social !== false,
    show_about: user?.show_about !== false,
    show_tip_jar: user?.show_tip_jar !== false,
  });
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [uploading, setUploading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [templates, setTemplates] = useState([]);

  useEffect(() => { client.get("/bio-templates").then(r => setTemplates(r.data)).catch(() => {}); }, []);
  const [previewTpl, setPreviewTpl] = useState(null);

  const save = async () => {
    try {
      const { data } = await client.put("/profile", {
        name: f.name, bio: f.bio, about: f.about, title: f.title, slug: f.slug,
        wallet_address: f.wallet_address, store_logo: f.store_logo,
        show_shop: f.show_shop, show_links: f.show_links, show_social: f.show_social,
        show_about: f.show_about, show_tip_jar: f.show_tip_jar,
      });
      saveAuth(localStorage.getItem("k_token"), data);
      toast.success("Saved"); refresh?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const applyTemplate = async (tpl) => {
    if (!window.confirm(`Apply the "${tpl.career}" preset? This will update your title, bio, and theme.`)) return;
    try {
      const { data } = await client.post("/bio-templates/apply", { template_id: tpl.id });
      saveAuth(localStorage.getItem("k_token"), data);
      setF(v => ({ ...v, title: data.title || v.title, bio: data.bio || v.bio }));
      if (tpl.music_platforms) {
        toast.success(`Applied "${tpl.career}" preset — now add your Spotify, Apple Music & Audiomack links`, {
          duration: 8000,
          action: { label: "ADD MUSIC LINKS", onClick: () => window.location.assign("/dashboard/social#music") },
        });
      } else {
        toast.success(`Applied "${tpl.career}" preset — check your public bio`);
      }
      refresh?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) return toast.error("Max 3MB");
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await client.post("/upload/product-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF(v => ({ ...v, store_logo: data.url }));
      toast.success("Store logo uploaded — click SAVE PROFILE to apply");
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    setUploadingLogo(false);
    e.target.value = "";
  };
  const uploadFile = async (file) => {
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { toast.error("Max 3MB — please pick a smaller image"); return; }
    if (!/^image\/(jpeg|jpg|png|webp|gif)$/.test(file.type)) { toast.error("Use JPG / PNG / WEBP / GIF"); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await client.post("/upload/avatar", form, { headers: { "Content-Type": "multipart/form-data" } });
      setF(prev => ({ ...prev, avatar: data.avatar }));
      toast.success(`Avatar uploaded (${(data.size_bytes / 1024).toFixed(0)} KB)`);
      refresh?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    setUploading(false);
  };
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) uploadFile(file);
  };
  const removeAvatar = async () => {
    try {
      await client.delete("/upload/avatar");
      setF(prev => ({ ...prev, avatar: "" }));
      toast.success("Avatar removed");
      refresh?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const changePw = async (e) => {
    e.preventDefault();
    if (pw.new_password !== pw.confirm) return toast.error("Passwords don't match");
    if (pw.new_password.length < 6) return toast.error("Minimum 6 characters");
    try {
      await client.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      toast.success("Password updated");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  return (
    <div>
      <PageHead eyebrow="SETTINGS" title="Your identity." />
      {previewTpl && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setPreviewTpl(null)} data-testid="bio-tpl-modal">
          <div onClick={e => e.stopPropagation()} className="w-full max-w-2xl max-h-[90vh] overflow-auto border border-white/10 bg-[#111318]">
            {/* Modal header */}
            <div className="border-b border-white/10 p-4 flex items-center justify-between sticky top-0 bg-[#111318]">
              <div>
                <div className="text-[10px] font-mono-k tracking-widest-k text-[#FACC15]">— PREVIEW · {previewTpl.eyebrow}</div>
                <div className="font-display text-xl">{previewTpl.career}</div>
              </div>
              <button onClick={() => setPreviewTpl(null)} className="k-btn text-xs" data-testid="bio-tpl-modal-close"><X size={12} /></button>
            </div>
            {/* Mini rendered bio preview */}
            <div
              className="p-8"
              style={{ background: previewTpl.theme?.bg_color || "#0A0D14", color: previewTpl.theme?.text_color || "#FFFFFF" }}
              data-testid="bio-tpl-preview-render"
            >
              <div className="max-w-md mx-auto">
                <div className="text-[10px] font-mono-k tracking-widest-k mb-2" style={{ color: previewTpl.theme?.accent_color || "#FACC15" }}>— {previewTpl.title_suggestion?.toUpperCase()}</div>
                <div className="text-3xl mb-3 leading-tight" style={{ fontFamily: `"${previewTpl.theme?.font_display || 'Clash Display'}", ui-sans-serif` }}>{user?.name || "Your Name"}</div>
                <p className="text-sm opacity-80 mb-6">{previewTpl.bio_suggestion}</p>
                <div className="space-y-2 mb-4">
                  {(previewTpl.starter_links || []).map((link, i) => (
                    <div key={i} className={`p-3 border ${previewTpl.theme?.card_style === "brutal" ? "border-2" : "border"} flex items-center gap-3`} style={{ borderColor: (previewTpl.theme?.accent_color || "#FACC15") + "60", background: "rgba(255,255,255,0.03)" }}>
                      {MUSIC_BY_ID[link.icon] ? (() => { const M = MUSIC_BY_ID[link.icon]; return <M.icon size={20} weight="fill" style={{ color: M.color }} />; })() : <span className="text-lg">{link.icon}</span>}
                      <span className="text-sm font-medium">{link.title}</span>
                    </div>
                  ))}
                </div>
                <div className="text-[10px] font-mono-k tracking-widest-k opacity-40 text-center">POWERED BY KORPORATE</div>
              </div>
            </div>
            {/* Modal footer */}
            <div className="border-t border-white/10 p-4 flex items-center gap-2 justify-end sticky bottom-0 bg-[#111318]">
              <button onClick={() => setPreviewTpl(null)} className="k-btn text-xs" data-testid="bio-tpl-modal-cancel">CANCEL</button>
              <button
                onClick={() => { applyTemplate(previewTpl); setPreviewTpl(null); }}
                className="k-btn primary text-xs"
                data-testid="bio-tpl-modal-apply"
              >
                <Check size={12} /> APPLY THIS PRESET
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="border border-white/10 bg-[#111318] p-6">
          <div className="text-xs font-mono-k tracking-widest-k text-[#FACC15] mb-4">— PROFILE PICTURE</div>
          <div
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            className={`border-2 border-dashed ${dragOver ? "border-[#FACC15] bg-[#FACC15]/5" : "border-white/20"} p-6 flex items-center gap-5 mb-4`}
            data-testid="avatar-dropzone"
          >
            {f.avatar ? (
              <img src={f.avatar} alt="" className="w-24 h-24 object-cover border-2 border-[#FACC15]" data-testid="avatar-preview" />
            ) : (
              <div className="w-24 h-24 border-2 border-white/20 bg-[#0A0D14] flex items-center justify-center font-display text-4xl text-[#FACC15]">
                {(f.name || user?.email || "K")[0]?.toUpperCase()}
              </div>
            )}
            <div className="flex-1">
              <label className="k-btn primary text-xs cursor-pointer inline-block" data-testid="avatar-upload-btn">
                {uploading ? "UPLOADING…" : (f.avatar ? "CHANGE PHOTO" : "UPLOAD PHOTO")}
                <input type="file" accept="image/jpeg,image/jpg,image/png,image/webp,image/gif" onChange={e => uploadFile(e.target.files?.[0])} className="hidden" data-testid="avatar-input" />
              </label>
              {f.avatar && <button onClick={removeAvatar} className="k-btn text-xs ml-2" data-testid="avatar-remove">REMOVE</button>}
              <div className="text-[10px] font-mono-k tracking-widest-k text-[#A1A1AA] mt-2">
                Drag & drop or click · JPG / PNG / WEBP / GIF · Max 3MB · Appears on your public bio and dashboard
              </div>
            </div>
          </div>
          <div className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA] mb-2">— OR PASTE AN IMAGE URL</div>
          <div className="flex gap-2">
            <input value={f.avatar.startsWith("data:") ? "" : f.avatar} onChange={e => setF({ ...f, avatar: e.target.value })} placeholder="https://…/avatar.jpg" className="k-input flex-1" data-testid="set-avatar-url" />
            <button onClick={async () => {
              try { await client.put("/profile", { avatar: f.avatar }); toast.success("Avatar URL saved"); refresh?.(); }
              catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
            }} className="k-btn text-xs" data-testid="set-avatar-url-save">SAVE URL</button>
          </div>
        </div>

        <div className="border border-white/10 bg-[#111318] p-6">
          <div className="text-xs font-mono-k tracking-widest-k text-[#FACC15] mb-4">— PROFILE</div>
          <div className="grid gap-4">
            <div><label className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA]">FULL NAME</label><input value={f.name} onChange={e => setF({ ...f, name: e.target.value })} className="k-input mt-1" data-testid="set-name" /></div>
            <div><label className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA]">TITLE / WORK FIELD</label><input value={f.title} onChange={e => setF({ ...f, title: e.target.value })} className="k-input mt-1" placeholder="e.g. Music Producer · Digital Marketer · Coach" data-testid="set-title" /></div>
            <div><label className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA]">SLUG</label><input value={f.slug} onChange={e => setF({ ...f, slug: e.target.value })} className="k-input mt-1" data-testid="set-slug" /></div>
            <div><label className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA]">BIO (one line under your name)</label><textarea rows={2} value={f.bio} onChange={e => setF({ ...f, bio: e.target.value })} className="k-input mt-1" data-testid="set-bio" /></div>
            <div><label className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA]">ABOUT (longer story — appears in an About section on your bio)</label><textarea rows={5} value={f.about} onChange={e => setF({ ...f, about: e.target.value })} className="k-input mt-1" placeholder="Your background, credentials, mission, or what makes you different." data-testid="set-about" /></div>
            <div><label className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA]">WALLET ADDRESS</label><input value={f.wallet_address} onChange={e => setF({ ...f, wallet_address: e.target.value })} className="k-input mt-1" placeholder="0x… / BTC / SOL" data-testid="set-wallet" /></div>
            <button onClick={save} className="k-btn primary justify-center" data-testid="set-save">SAVE PROFILE</button>
          </div>
        </div>

        {/* Bio templates gallery */}
        <div className="border border-white/10 bg-[#111318] p-6 lg:col-span-2" data-testid="bio-templates">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-xs font-mono-k tracking-widest-k text-[#FACC15]">— CAREER PRESETS</div>
              <div className="font-display text-xl">One click — full brand kit.</div>
            </div>
            <div className="text-[10px] font-mono-k text-[#A1A1AA]">Applies a title, bio, and matching theme. You can edit any of it after.</div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-0 border-t border-l border-white/10">
            {templates.map(t => (
              <div key={t.id} className="border-r border-b border-white/10 bg-[#0A0D14] flex flex-col" data-testid={`bio-tpl-${t.id}`}>
                <button
                  onClick={() => setPreviewTpl(t)}
                  className="text-left p-4 hover:bg-[#FACC15]/5 transition-colors group flex-1"
                  data-testid={`bio-tpl-preview-${t.id}`}
                >
                  <div className="w-8 h-8 mb-2" style={{ background: t.theme?.accent_color || "#FACC15" }} />
                  <div className="text-[10px] font-mono-k tracking-widest-k text-[#FACC15] mb-1">— {t.eyebrow}</div>
                  <div className="font-display text-lg group-hover:text-[#FACC15] transition-colors">{t.career}</div>
                  <div className="text-[10px] font-mono-k text-[#A1A1AA] mt-1 line-clamp-2">{t.title_suggestion}</div>
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Section toggles */}
        <div className="border border-white/10 bg-[#111318] p-6" data-testid="section-toggles">
          <div className="text-xs font-mono-k tracking-widest-k text-[#FACC15] mb-3">— BIO SECTIONS</div>
          <div className="text-[10px] font-mono-k text-[#A1A1AA] mb-3">Hide / show sections on your public bio page. Changes save when you click SAVE PROFILE.</div>
          {[
            { key: "show_links", label: "LINKS" },
            { key: "show_shop", label: "SHOP" },
            { key: "show_social", label: "SOCIAL ICONS" },
            { key: "show_about", label: "ABOUT" },
            { key: "show_tip_jar", label: "TIP JAR" },
          ].map(t => (
            <label key={t.key} className="flex items-center justify-between py-2 border-b border-white/5 last:border-b-0 cursor-pointer" data-testid={`toggle-${t.key}`}>
              <span className="text-xs font-mono-k tracking-widest-k">{t.label}</span>
              <input type="checkbox" checked={!!f[t.key]} onChange={e => setF({ ...f, [t.key]: e.target.checked })} className="w-5 h-5 accent-[#FACC15]" />
            </label>
          ))}
        </div>

        {/* Store logo */}
        <div className="border border-white/10 bg-[#111318] p-6" data-testid="store-logo-panel">
          <div className="text-xs font-mono-k tracking-widest-k text-[#FACC15] mb-3">— STORE LOGO</div>
          <div className="text-[10px] font-mono-k text-[#A1A1AA] mb-3">Optional — brand your shop with a separate logo (shown above your product grid).</div>
          {f.store_logo ? (
            <div className="flex items-center gap-3 mb-3">
              <img src={f.store_logo} alt="" className="w-24 h-24 object-contain border border-white/10 bg-[#0A0D14] p-2" data-testid="store-logo-preview" />
              <button onClick={() => setF({ ...f, store_logo: "" })} className="k-btn text-xs" data-testid="store-logo-clear"><X size={12} /> REMOVE</button>
            </div>
          ) : null}
          <label className={`k-btn text-xs cursor-pointer ${uploadingLogo ? "opacity-60" : ""}`} data-testid="store-logo-upload">
            <UploadSimple size={14} /> {uploadingLogo ? "UPLOADING…" : "UPLOAD STORE LOGO"}
            <input type="file" accept="image/*" onChange={uploadLogo} disabled={uploadingLogo} className="hidden" />
          </label>
        </div>

        <div className="border border-white/10 bg-[#111318] p-6">
          <div className="text-xs font-mono-k tracking-widest-k text-[#FACC15] mb-4">— CHANGE PASSWORD</div>
          <form onSubmit={changePw} className="grid gap-4">
            <div><label className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA]">CURRENT PASSWORD</label><input type="password" required value={pw.current_password} onChange={e => setPw({ ...pw, current_password: e.target.value })} className="k-input mt-1" data-testid="pw-current" /></div>
            <div><label className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA]">NEW PASSWORD</label><input type="password" minLength={6} required value={pw.new_password} onChange={e => setPw({ ...pw, new_password: e.target.value })} className="k-input mt-1" data-testid="pw-new" /></div>
            <div><label className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA]">CONFIRM NEW PASSWORD</label><input type="password" minLength={6} required value={pw.confirm} onChange={e => setPw({ ...pw, confirm: e.target.value })} className="k-input mt-1" data-testid="pw-confirm" /></div>
            <button className="k-btn primary justify-center" data-testid="pw-save">UPDATE PASSWORD</button>
          </form>
          <div className="mt-6 border-t border-white/10 pt-4">
            <div className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA] mb-2">EMAIL STATUS</div>
            <div className="flex items-center gap-2 text-sm font-mono-k">
              {user?.email_verified ? (
                <><span className="w-2 h-2 bg-[#FACC15]" /> <span>{user?.email} — verified</span></>
              ) : (
                <><span className="w-2 h-2 bg-red-400" /> <span>{user?.email} — not verified</span></>
              )}
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 grid gap-6">
          <TwoFactorCard user={user} refresh={refresh} />
          <PushCard />
        </div>
      </div>
    </div>
  );
};

// -------- Root Dashboard --------
const Dashboard = () => {
  const nav = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(getUser());
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("k_sidebar_collapsed") === "1");
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => { localStorage.setItem("k_sidebar_collapsed", collapsed ? "1" : "0"); }, [collapsed]);
  // Auto-close mobile drawer on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  const refresh = async () => {
    try { const { data } = await client.get("/auth/me"); setUser(data); saveAuth(localStorage.getItem("k_token"), data); }
    catch (err) { if (process.env.NODE_ENV !== "production") console.warn("auth refresh failed:", err); }
  };
  useEffect(() => { refresh(); }, []);
  const logout = () => { clearAuth(); nav("/login"); };
  if (!user) return <Navigate to="/login" replace />;

  const resendVerify = async () => {
    try { await client.post("/auth/resend-verification"); toast.success("Verification email sent"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="flex min-h-screen bg-[#0A0D14] text-white">
      <TwoFactorNudge user={user} />
      <Sidebar user={user} onLogout={logout} collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      <main className="flex-1 min-w-0 flex flex-col">
        <MobileBar onOpen={() => setMobileOpen(true)} user={user} />
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-6xl mx-auto p-4 md:p-8">
            {user && user.email && user.email_verified === false && (
              <div className="border border-[#FACC15]/40 bg-[#FACC15]/10 p-3 mb-6 flex items-center gap-3 flex-wrap" data-testid="verify-banner">
                <EnvelopeSimple size={18} className="text-[#FACC15] shrink-0" />
                <span className="text-xs md:text-sm font-mono-k flex-1 min-w-[200px]">Verify your email — <strong>{user.email}</strong> — to unlock payouts and referral commissions.</span>
                <button onClick={resendVerify} className="k-btn text-[10px] py-2" data-testid="btn-resend-verify">RESEND EMAIL</button>
              </div>
            )}
            <Routes>
              <Route index element={<Overview user={user} />} />
              <Route path="links" element={<Links />} />
              <Route path="appearance" element={<Appearance user={user} />} />
              <Route path="shop" element={<Shop />} />
              <Route path="leads" element={<Leads />} />
              <Route path="funnels" element={<Funnels />} />
              <Route path="campaigns" element={<Campaigns />} />
              <Route path="ai" element={<AI />} />
              <Route path="social" element={<Social />} />
              <Route path="wallet" element={<WalletView user={user} refresh={refresh} />} />
              <Route path="referrals" element={<Referrals />} />
              <Route path="analytics" element={<Analytics />} />
              <Route path="pricing" element={<Pricing user={user} refresh={refresh} />} />
              <Route path="settings" element={<Settings user={user} refresh={refresh} />} />
              {user?.is_admin && <Route path="admin/payouts" element={<AdminPayouts />} />}
              {user?.is_admin && <Route path="admin/fees" element={<AdminFees />} />}
              {user?.is_admin && <Route path="admin/referrals" element={<AdminReferrals />} />}
              {user?.is_admin && <Route path="admin/integrations" element={<AdminIntegrations />} />}
              {user?.is_admin && <Route path="admin/promo-codes" element={<AdminPromoCodes />} />}
            </Routes>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
